import { Component, Fragment } from "preact";
import type { ComponentChildren, JSX } from "preact";
import type IndoorPanelControl from "./indoor-panel-control";
import Autocomplete from "./autocomplete";
import type { AutocompleteItem } from "./autocomplete";
import Icon from "./icons";
import pointOnFeature from "@turf/point-on-feature";
import Switch from "./switch";
import OSMOpeningHours from "./utils/osm-opening-hours";
import { Events as EVENTS } from "./utils/events";
import { getLocale, getLocaleLang } from "helpers/locale";
import { getConfig } from "configuration/config";
import { showToast, displayBuilding } from "utils/utils";
/**
 * @private
 */
type Props = {
  controller: IndoorPanelControl;
};
/**
 * @private
 */
type PanelState = {
  value: string;
  isNavigationMode: boolean;
  searchedFeatures: Array<woosmap.map.GeoJSONFeature>;
  searchedDestinations: Array<woosmap.map.GeoJSONFeature>;
  searchedOrigins: Array<woosmap.map.GeoJSONFeature>;
  directionsResult: woosmap.map.IndoorDirectionResult | undefined | null;
  showPanel: boolean;
  showItineraryResults: boolean;
  activeTab: string;
  mode: string;
  routeErrorMessage: string | JSX.Element;
  stepSelected: string;
  isLoading: boolean;
  allFeaturesLoaded: boolean;
  allFeatures: Array<woosmap.map.GeoJSONFeature>;
  levelClicked: Record<number, boolean>;
  userLocation: woosmap.map.GeoJSONFeature | undefined;
  selectedDestination: woosmap.map.GeoJSONFeature | undefined;
  selectedOrigin: woosmap.map.GeoJSONFeature | undefined;
  selectedFeature: woosmap.map.GeoJSONFeature | undefined;
  wheelChairEnabled: boolean;
  openingHoursClicked: boolean;
  selectedStop: woosmap.map.GeoJSONFeature | undefined;
  searchedStops: Array<woosmap.map.GeoJSONFeature>;
  selectedWaypoints: Array<woosmap.map.GeoJSONFeature>;
  showRouteBuilderSummary: boolean;
  removingWaypointId: string | number | null;
  showExitGates: boolean;
  selectedExitGate: woosmap.map.GeoJSONFeature | undefined | string;
  allExitGatesLoaded: boolean;
  allExitGates: Array<woosmap.map.GeoJSONFeature>;
  isBuildingRoute: boolean;
  routeBuilderView: string;
  isDragging: boolean;
  showRouteBuilderWarning: boolean;
  widgetMapMode: string;
  selectAllFavorites: boolean;
  shopsMarkedForVisit?: woosmap.map.GeoJSONFeatureCollection;
  isUserUnauthorized: boolean;
  staticToastDismissed: boolean;
  routeBuilderToastDismissed: boolean;
  itinerarySource: string;
};

/**
 * @private
 */
export default class IndoorPanel extends Component<Props, PanelState> {
  indoorService: woosmap.map.IndoorService;
  controller: IndoorPanelControl;
  map: woosmap.map.Map;
  panelHeight: number;
  panelWidth: number;
  private _inflightRouteSignature: string | null = null;
  config: any;
  waypointsMarkers: woosmap.map.Marker[] = [];
  panelRef?: HTMLDivElement;
  startY = 0;
  routeBuilderWarning: string = "";
  shopsToVisit?: woosmap.map.GeoJSONFeatureCollection;

  constructor(props: Props) {
    super();
    this.state = {
      value: "",
      isNavigationMode: false,
      searchedFeatures: [],
      searchedDestinations: [],
      searchedOrigins: [],
      allFeatures: [],
      allFeaturesLoaded: false,
      mode: "default",
      activeTab: "level",
      showPanel: false,
      showItineraryResults: false,
      routeErrorMessage: "",
      stepSelected: "",
      directionsResult: undefined,
      isLoading: false,
      levelClicked: {},
      userLocation: undefined,
      selectedFeature: undefined,
      selectedDestination: undefined,
      selectedOrigin: undefined,
      wheelChairEnabled: false,
      openingHoursClicked: false,
      selectedStop: undefined,
      searchedStops: [],
      showRouteBuilderSummary: false,
      selectedWaypoints: [],
      removingWaypointId: null,
      showExitGates: false,
      selectedExitGate: undefined,
      allExitGatesLoaded: false,
      allExitGates: [],
      isBuildingRoute: false,
      routeBuilderView: "default",
      isDragging: false,
      showRouteBuilderWarning: false,
      widgetMapMode: "general",
      selectAllFavorites: false,
      shopsMarkedForVisit: undefined,
      isUserUnauthorized: false,
      staticToastDismissed: false,
      routeBuilderToastDismissed: false,
      itinerarySource: "",
    };
    this.indoorService = new window.woosmap.map.IndoorService();

    this.controller = props.controller;
    this.map = (props.controller._renderer as any).map;
    this.panelHeight = 0;
    this.panelWidth = 0;
    this.renderHeader = this.renderHeader.bind(this);
    this.renderPanelContent = this.renderPanelContent.bind(this);
    this.renderLevel = this.renderLevel.bind(this);
    this.renderMall = this.renderMall.bind(this);
    this.renderItinerary = this.renderItinerary.bind(this);
    this.renderRouteBuilder = this.renderRouteBuilder.bind(this);
    this.autocomplete = this.autocomplete.bind(this);
    this.swapOriginAndDestination = this.swapOriginAndDestination.bind(this);
    this.findRoute = this.findRoute.bind(this);
    this.showRouteBuilder = this.showRouteBuilder.bind(this);
    this.groupInstructionsByLevel = this.groupInstructionsByLevel.bind(this);
    this.highlightStep = this.highlightStep.bind(this);
    this.onDisabilitySwitchChange = this.onDisabilitySwitchChange.bind(this);
    this.launchNavigation = this.launchNavigation.bind(this);
    this.onAutoCompleteClose = this.onAutoCompleteClose.bind(this);
    this.onExitGateOpted = this.onExitGateOpted.bind(this);
    this.onExitGateSelected = this.onExitGateSelected.bind(this);
    this.onBuildRouteWithWaypoints = this.onBuildRouteWithWaypoints.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onHideWarningBanner = this.onHideWarningBanner.bind(this);
    this.cancelRouteBuilding = this.cancelRouteBuilding.bind(this);
    this.editStops = this.editStops.bind(this);

    this.controller._renderer.addListener(
      "indoor_feature_selected",
      this.selectFeature.bind(this),
    );
    this.controller._renderer.addListener(
      "indoor_user_location",
      this.setUserLocation.bind(this),
    );
    this.controller.addListener("indoor_stop_navigation", () => {
      this.setState({ isNavigationMode: false });
    });
    this.controller.addListener(
      "widget_open_itinerary_panel",
      (
        origin?: woosmap.map.GeoJSONFeature | Object | string,
        destination?: woosmap.map.GeoJSONFeature | Object | string,
        source?: string,
      ) => {
        this.showItineraryPanel(origin, destination, source);
      },
    );
    this.controller.addListener(
      "indoor_widget_map_changed",
      (mapmode: string, shops?: woosmap.map.GeoJSONFeatureCollection, isUnauthorized?: boolean) => {
        const { isNavigationMode, mode } = this.state;
        if (!isNavigationMode) {
          this.cancelRouteBuilding();
        }
        const filterShopsByStatus = shops?.features.filter(
          (feature) =>
            (feature.properties as any)["visit_status"] === "tovisit",
        );
        if (filterShopsByStatus && filterShopsByStatus.length > 0) {
          this.shopsToVisit = {
            type: "FeatureCollection",
            features: filterShopsByStatus,
          };
        } else {
          this.shopsToVisit = undefined;
        }
        if (mode === "waypoints") {
          this.setState({
            shopsMarkedForVisit: shops,
          });
        }
        this.setState({
          widgetMapMode: mapmode,
          selectedOrigin: undefined,
          selectedDestination: undefined,
          selectedFeature: undefined,
          isUserUnauthorized: !!isUnauthorized,
          staticToastDismissed: false,
        });
      },
    );
  }
  componentDidUpdate(_prevProps: Props, prevState: PanelState) {
    const { selectedFeature, selectedOrigin } = this.state;
    const {
      mode,
      allFeaturesLoaded,
      showPanel,
      wheelChairEnabled,
      allExitGatesLoaded,
      widgetMapMode,
      shopsMarkedForVisit,
      selectedWaypoints,
    } = this.state;
    if (
      prevState.selectedFeature !== selectedFeature &&
      prevState.selectedFeature !== undefined &&
      mode !== "itinerary"
    ) {
      this.setState({
        directionsResult: undefined,
        stepSelected: "",
        routeErrorMessage: "",
        mode: "default",
      });
    }
    const panels = document.getElementsByClassName("woosmap-indoor-panel");
    const panel =
      panels.length > 0
        ? document.getElementsByClassName("woosmap-indoor-panel")[0]
        : null;
    if (panel && showPanel && panel.classList.contains("hide")) {
      panel.classList.remove("hide");
      panel.classList.add("show");
    } else if (panel && !showPanel && panel.classList.contains("show")) {
      panel.classList.remove("show");
      panel.classList.add("hide");
    }
    if (!allFeaturesLoaded && mode === "level" && prevState.mode !== mode) {
      this.loadAllFeatures();
    }
    if (mode === "itinerary" && prevState.mode !== mode) {
      this.findRoute();
    }
    if (
      mode === "itinerary" &&
      prevState.wheelChairEnabled !== wheelChairEnabled
    ) {
      if (wheelChairEnabled) {
        this.controller.setDirectionsMode("wheelchair");
      } else {
        this.controller.setDirectionsMode("");
      }
      this.findRoute();
    }
    if (
      mode === "itinerary" &&
      (prevState.selectedFeature !== selectedFeature ||
        prevState.selectedOrigin !== selectedOrigin)
    ) {
      this.findRoute();
    }
    if (
      !allExitGatesLoaded &&
      mode === "waypoints" &&
      prevState.mode !== mode
    ) {
      this.loadExitGates();
    }
    if (
      mode === "waypoints" &&
      prevState.mode !== mode &&
      shopsMarkedForVisit === undefined
    ) {
      this.setState({
        shopsMarkedForVisit: this.shopsToVisit,
      });
    }
    if (
      widgetMapMode === "favorites" &&
      mode === "waypoints" &&
      prevState.shopsMarkedForVisit?.features.length !==
      shopsMarkedForVisit?.features.length
    ) {
      if (this.shopsToVisit) {
        this.prepareShopsToVisitForRouteBuilding(this.shopsToVisit);
      }
    }
    if (
      mode === "waypoints" &&
      prevState.selectedWaypoints?.length !== selectedWaypoints?.length
    ) {
      this.controller.setSelectedWaypoints(selectedWaypoints);
    }
  }
  componentDidMount() {
    const headerContent = document.getElementsByClassName(
      "woosmap-panel__content__header",
    )[0];
    const bodyContent = document.getElementsByClassName(
      "woosmap-panel__content__body",
    )[0];
    const searchBarContainer = document.getElementsByClassName(
      "woosmap-panel__header",
    )[0];
    if (searchBarContainer && headerContent && bodyContent) {
      // eslint-disable-next-line max-len
      this.panelHeight =
        headerContent.clientHeight +
        bodyContent.clientHeight +
        searchBarContainer.clientHeight;
      this.panelWidth = searchBarContainer.clientWidth;
    }
  }
  selectFeature(selectedFeature: woosmap.map.GeoJSONFeature) {
    const { isNavigationMode, mode } = this.state;
    if (!isNavigationMode && mode !== "itinerary") {
      this.setState({
        selectedFeature,
        selectedDestination: selectedFeature,
        showPanel: !!selectedFeature,
      });
    }
  }

  setUserLocation() {
    const userLocation = this.controller._renderer.getUserLocation();
    const { selectedOrigin } = this.state;
    if (!userLocation) {
      return;
    }
    const updatedOrigin = {
      type: "Feature",
      id: -1,
      geometry: {
        type: "Point",
        coordinates: [userLocation.position.lng, userLocation.position.lat],
      },
      properties: {
        level: userLocation.level.toString(),
        name: getLocale().indoor["Your Location"],
        pk: "-1",
      },
    };
    if (selectedOrigin) {
      if (selectedOrigin.id && parseInt(String(selectedOrigin.id)) < 0) {
        this.setState({ selectedOrigin: updatedOrigin as any });
      }
    } else {
      this.setState({ selectedOrigin: updatedOrigin as any });
    }
    this.setState({ userLocation: updatedOrigin as any });
  }

  loadAllFeatures() {
    const venue = this.controller._renderer.getVenue();
    this.setState({ allFeaturesLoaded: false });
    const callback = (
      featuresCollection: woosmap.map.GeoJSONFeatureCollection,
    ) => {
      this.setState({
        allFeatures: featuresCollection.features,
        allFeaturesLoaded: true,
      });
    };
    if (venue) {
      this.indoorService.search(
        venue.venue_id,
        "",
        callback,
        undefined,
        undefined,
        this.controller.getBaseFilter(),
      );
    }
  }
  loadExitGates() {
    const venue = this.controller._renderer.getVenue();
    this.setState({ allExitGatesLoaded: false });
    const callback = (
      featuresCollection: woosmap.map.GeoJSONFeatureCollection,
    ) => {
      this.setState({
        allExitGates: featuresCollection.features,
        allExitGatesLoaded: true,
      });
    };
    if (venue) {
      this.indoorService.search(
        venue.venue_id,
        "",
        callback,
        undefined,
        undefined,
        "multi_nav_exit:=yes",
      );
    }
  }
  autocomplete(
    stateKey: string,
    stateTextKey: string,
    value: string,
  ): Promise<any> {
    return new Promise((resolve) => {
      const venue = this.controller._renderer.getVenue();
      let result;
      if (venue && (this.state as any)[stateTextKey] !== value) {
        const userLocation = this.controller._renderer.getUserLocation();
        const fromLocation =
          userLocation && this.controller.getAutocompleteWithDistance()
            ? `${userLocation.position.lat},${userLocation.position.lng},${userLocation.level}`
            : null;
        const queryParams = {
          fromLocation: userLocation
            ? { position: userLocation.position, level: userLocation.level }
            : null,
          filter: this.controller.getBaseFilter(),
        };
        const maxResponses = this.controller.getAutocompleteMaxResponses();
        const callback = (results: any) => {
          const newState: Record<string, any> = {};
          newState[stateKey] = results.predictions ? results.predictions : [];
          newState[stateTextKey] = value;
          this.setState(newState as any);
          const { userLocation } = this.state;
          if (userLocation) {
            const user = {
              id: -1,
              name: getLocale().indoor["Your Location"],
              level: parseInt((userLocation.properties as any)["level"]),
              building: undefined,
              ref: undefined,
              distance: undefined,
            };
            results.predictions.unshift(user);
          }
          result = this.autocompleteItems(results.predictions);
          resolve(result);
          if (value.length > 0) {
            /**
             * This event is fired when the user performs any widget specific activities.
             * @event IndoorWidget#indoor_widget_analytics
             * @property {string} event AUTOCOMPLETE.
             * @property {*} args The search query & the list of predictions.
             */
            this.controller.trigger(this, "indoor_widget_analytics", [
              {
                event: EVENTS.AUTOCOMPLETE,
                args: { query: value, queryParams, results: result },
              },
            ]);
          }
        };
        const filter =
          stateKey === "searchedOrigins"
            ? null
            : this.controller.getBaseFilter();
        this.indoorService.autocomplete(
          venue.venue_id,
          value,
          callback,
          undefined,
          undefined,
          fromLocation,
          filter,
          maxResponses,
        );
      }
    });
  }
  onAutoCompleteClose(stateKey: string) {
    const newState: Record<string, any> = {};
    newState[stateKey] = undefined;
    this.setState(newState as any);
  }
  autocompleteItems(
    results: Array<woosmap.map.Indoor.Prediction>,
  ): Array<AutocompleteItem> {
    return results.map((poi) => {
      const venue = this.controller._renderer.getVenue();
      let building: any[] = [];
      if (venue && venue.buildings && venue.buildings.length > 0) {
        building = venue.buildings.filter(
          (current) => current.ref === poi.building,
        );
      }
      let buildingName = null;
      if (building && building.length > 0) {
        const localizedName = building[0]["localized_name"];
        if (localizedName && localizedName[getLocaleLang().toLowerCase()]) {
          buildingName = localizedName[getLocaleLang().toLowerCase()];
        } else {
          buildingName = building[0].name;
        }
      }
      return {
        id: poi.id || 0,
        name: (poi as any)[`name:${getLocaleLang().toLowerCase()}`]
          ? (poi as any)[`name:${getLocaleLang().toLowerCase()}`]
          : (poi as any)["name"],
        level: poi.level.toString(),
        distance: poi["distance"],
        building: buildingName,
        ref: poi["ref"],
      };
    });
  }
  swapOriginAndDestination() {
    const { selectedOrigin, selectedDestination } = this.state;
    if (selectedOrigin && selectedDestination) {
      this.setState(
        {
          selectedOrigin: selectedDestination,
          selectedDestination: selectedOrigin,
        },
        this.findRoute,
      );
    }
  }

  findRoute() {
    const { selectedOrigin, selectedDestination, selectedWaypoints } =
      this.state;
    const venue = this.controller._renderer.getVenue();
    if (
      venue &&
      selectedOrigin &&
      selectedDestination &&
      selectedOrigin.id !== selectedDestination.id
    ) {
      this.controller._renderer.highlightStep(null);
      this.controller.setSelectedDestination(selectedDestination);
      this.setState({ stepSelected: "", isLoading: true });
      let origin;
      let originLevel;
      let originId: any = selectedOrigin.id;
      if (selectedOrigin.id === -1 || selectedOrigin.id === 0) {
        originId = null;
        const centrePointOrigin = pointOnFeature(selectedOrigin);
        origin = new woosmap.map.LatLng(
          centrePointOrigin.geometry.coordinates[1],
          centrePointOrigin.geometry.coordinates[0],
        );
        originLevel = parseInt((selectedOrigin.properties as any)["level"]);
      }

      let destination;
      let destinationLevel;
      let destinationId: any = selectedDestination.id;
      if ((destinationId && destinationId === -1) || destinationId === 0) {
        destinationId = null;
        const centrePointDestination = pointOnFeature(selectedDestination);
        destination = new woosmap.map.LatLng(
          centrePointDestination.geometry.coordinates[1],
          centrePointDestination.geometry.coordinates[0],
        );
        destinationLevel = parseInt(
          (selectedDestination.properties as any)["level"],
        );
      }
      let requestParams: woosmap.map.IndoorDirectionRequest = {
        venueId: venue.venue_id,
        origin,
        originLevel,
        originId,
        destination,
        destinationLevel,
        destinationId,
        language: getLocaleLang().toLowerCase(),
        units: this.controller.getUnits(),
        mode: this.controller.getDirectionsMode(),
        avoid: this.controller.getZonesToAvoid(),
      };
      let selectedStopsOnRoute: string = "";
      if (selectedWaypoints && selectedWaypoints.length > 0) {
        selectedWaypoints.map((stop) => {
          if (stop.id && stop.id !== -1 && stop.id !== 0) {
            selectedStopsOnRoute = selectedStopsOnRoute.concat(stop.id + "|");
          }
        });
      }
      if (selectedStopsOnRoute.length > 0) {
        requestParams = {
          ...requestParams,
          waypoints: selectedStopsOnRoute.slice(0, -1),
          optimize: true,
        };
      }

      // Build a stable signature of this request. If an identical request is
      // already in flight (e.g. componentDidUpdate fires Block 1 and Block 3
      // in the same tick for a list-row itinerary click), skip the duplicate.
      const waypointSig = (selectedWaypoints ?? [])
        .map((w) => String(w.id))
        .join("|");
      const signature = JSON.stringify({
        v: requestParams.venueId,
        oId: requestParams.originId,
        o: origin ? [origin.lat(), origin.lng(), originLevel] : null,
        dId: requestParams.destinationId,
        d: destination ? [destination.lat(), destination.lng(), destinationLevel] : null,
        w: waypointSig,
        m: requestParams.mode,
        a: requestParams.avoid,
      });
      if (this._inflightRouteSignature === signature) {
        return;
      }
      this._inflightRouteSignature = signature;

      this.indoorService.directions(
        requestParams,
        (result: woosmap.map.IndoorDirectionResult) => {
          if (result) {
            this._inflightRouteSignature = null;
            this.setState({
              directionsResult: result,
              routeErrorMessage: "",
              isLoading: false,
              isBuildingRoute: false,
            });
            if (selectedWaypoints && selectedWaypoints.length > 0) {
              this.setState({ routeBuilderView: "results" });
            } else {
              this.setState({ mode: "itinerary" });
            }
            const padding = this.controller._container
              ? this.controller._container.clientWidth
              : 0;
            this.controller._renderer.setDirections(result, padding);
            this.addCustomMarkersForWaypoints(result);
            /**
             * This event is fired when the user performs any widget specific activities.
             * @event IndoorWidget#indoor_widget_analytics
             * @property {string} event FIND_ROUTE.
             * @property {*} args The origin and destination.
             */
            this.controller.trigger(this, "indoor_widget_analytics", [
              {
                event: EVENTS.SELECT_DIRECTION,
                args: {
                  origin: selectedOrigin,
                  destination: selectedDestination,
                  source: this.state.itinerarySource || "main_map",
                },
              },
            ]);
          } else {
            this._inflightRouteSignature = null;
            this.setState({
              routeErrorMessage: (
                <span>{getLocale().indoor["No route found"]}</span>
              ),
              directionsResult: undefined,
              isLoading: false,
            } as any);
            this.controller._renderer.setDirections(null);
          }
        },
      );
    }
  }
  groupInstructionsByLevel(steps: any[]): any[] {
    const instructionsByLevel: any[] = [];
    let lastStepsByLevel: any = { level: null };
    steps.forEach((step: any) => {
      if (lastStepsByLevel.level !== step.properties["level"]) {
        lastStepsByLevel = { level: step.properties["level"], steps: [] };
        instructionsByLevel.push(lastStepsByLevel);
      }
      lastStepsByLevel.steps.push(step);
    });
    return instructionsByLevel;
  }
  composeLevelTransition(
    originLevel: number,
    destinationLevel: number,
  ): Object {
    const level = Math.abs(destinationLevel - originLevel);
    let content = level <= 1 ? "level" : "levels";
    content = (getLocale().indoor as any)[content];
    const direction =
      originLevel === destinationLevel
        ? ""
        : originLevel > destinationLevel
          ? getLocale().indoor.down
          : getLocale().indoor.up;
    const floorTransition = {
      level,
      content,
      direction,
    };
    return floorTransition;
  }
  convertDurationToMins(value: number): string {
    const minute = Math.round(value / 60);
    return minute <= 1 ? `${minute} min` : `${minute} mins`;
  }
  highlightStep(step: woosmap.map.GeoJSONFeature) {
    this.setState({ stepSelected: (step.properties as any)["id"] });
    this.controller._renderer.highlightStep(step);
  }
  launchNavigation() {
    const { selectedOrigin, selectedDestination } = this.state;
    /**
     * This event is fired when the user performs any widget specific activities.
     * @event IndoorWidget#indoor_widget_analytics
     * @property {string} event NAVIGATION_CLICK.
     * @property {*} args The origin and destination.
     */
    this.controller.trigger(this, "indoor_widget_analytics", [
      {
        event: EVENTS.NAVIGATION_CLICK,
        args: { origin: selectedOrigin, destination: selectedDestination },
      },
    ]);
    this.controller.launchNavigation();
    this.setState({ isNavigationMode: true });
  }
  clearRouteAndItinerary() {
    this.controller._renderer.setDirections(null);
    this.controller._renderer.highlightStep(null);
    this.setState({
      directionsResult: undefined,
      stepSelected: "",
      routeErrorMessage: "",
    } as any);
  }

  toggleSteps(id: number) {
    const { levelClicked } = this.state;
    (levelClicked as any)[id] = (levelClicked as any)[id] === false;
    const newLevelClicked = { ...levelClicked };
    this.setState({
      levelClicked: newLevelClicked,
    } as any);
  }
  toggleOpeningHrs() {
    const { openingHoursClicked } = this.state;
    this.setState({ openingHoursClicked: !openingHoursClicked });
  }
  onDisabilitySwitchChange() {
    const { wheelChairEnabled } = this.state;
    this.setState({ wheelChairEnabled: !wheelChairEnabled });
  }
  onPhoneClicked(phone: string) {
    window.open(`tel:${phone}`, "_self");
    /**
     * This event is fired when the user performs any widget specific activities.
     * @event IndoorWidget#indoor_widget_analytics
     * @property {string} event PHONE_CLICKED.
     * @property {*} args The phone number of the selected POI.
     */
    this.controller.trigger(this, "indoor_widget_analytics", [
      { event: EVENTS.PHONE_CLICK, args: phone },
    ]);
  }
  onWebsiteClicked(website: string) {
    /**
     * This event is fired when the user performs any widget specific activities.
     * @event IndoorWidget#indoor_widget_analytics
     * @property {string} event WEBSITE_CLICKED.
     * @property {*} args The website URL of the selected POI.
     */
    this.controller.trigger(this, "indoor_widget_analytics", [
      { event: EVENTS.WEBSITE_CLICK, args: website },
    ]);
  }
  showItineraryPanel(
    origin?: woosmap.map.GeoJSONFeature | Object | string,
    destination?: woosmap.map.GeoJSONFeature | Object | string,
    source?: string,
  ) {
    let defaultStateParams: any = {
      mode: "itinerary",
      showPanel: true,
      wheelChairEnabled: this.controller.getDirectionsMode() === "wheelchair",
    };
    let stateParamsToApply = defaultStateParams;
    if (origin) {
      switch (typeof origin) {
        case "string":
          // eslint-disable-next-line no-case-declarations
          const arrOriginParams = origin.split(",");
          if (arrOriginParams && arrOriginParams.length > 1) {
            const originFeature: any = {
              type: "Feature",
              id: 0,
              geometry: {
                type: "Point",
                coordinates: [
                  parseFloat(arrOriginParams[1]),
                  parseFloat(arrOriginParams[0]),
                ],
              },
              properties: {
                level: arrOriginParams[2],
                name: "Custom Location",
              },
            };
            stateParamsToApply = {
              ...defaultStateParams,
              selectedOrigin: originFeature,
            };
          }
          break;
        case "object":
          const originObj = origin as any;
          if (
            originObj.type &&
            originObj.type === "Feature" &&
            originObj.geometry
          ) {
            stateParamsToApply = {
              ...defaultStateParams,
              selectedOrigin: origin,
            };
          } else {
            const userLocationAsOrigin: any = {
              type: "Feature",
              id: -1,
              geometry: {
                type: "Point",
                coordinates: [originObj.position.lng, originObj.position.lat],
              },
              properties: {
                level: originObj.level.toString(),
                name: getLocale().indoor["Your Location"],
                pk: "-1",
              },
            };
            stateParamsToApply = {
              ...defaultStateParams,
              selectedOrigin: userLocationAsOrigin,
            };
          }
      }
      defaultStateParams = stateParamsToApply;
    } else {
      this.setState({ selectedOrigin: undefined });
    }
    if (destination) {
      switch (typeof destination) {
        case "string":
          // eslint-disable-next-line no-case-declarations
          const arrDestinationParams = destination.split(",");
          if (arrDestinationParams && arrDestinationParams.length > 1) {
            const destFeature: any = {
              type: "Feature",
              id: 0,
              geometry: {
                type: "Point",
                coordinates: [
                  parseFloat(arrDestinationParams[1]),
                  parseFloat(arrDestinationParams[0]),
                ],
              },
              properties: {
                level: arrDestinationParams[2],
                name: "Custom Location",
              },
            };
            stateParamsToApply = {
              ...defaultStateParams,
              selectedDestination: destFeature,
              selectedFeature: destFeature,
            };
          }
          break;
        case "object":
          const destObj = destination as any;
          if (destObj.type && destObj.type === "Feature" && destObj.geometry) {
            stateParamsToApply = {
              ...defaultStateParams,
              selectedDestination: destination,
              selectedFeature: destination,
            };
          } else {
            const userLocationAsDest: any = {
              type: "Feature",
              id: -1,
              geometry: {
                type: "Point",
                coordinates: [destObj.position.lng, destObj.position.lat],
              },
              properties: {
                level: destObj.level.toString(),
                name: getLocale().indoor["Your Location"],
                pk: "-1",
              },
            };
            stateParamsToApply = {
              ...defaultStateParams,
              selectedDestination: userLocationAsDest,
              selectedFeature: userLocationAsDest,
            };
          }
          break;
      }
    } else {
      this.setState({
        selectedDestination: undefined,
        selectedFeature: undefined,
      });
    }
    this.setState({ ...stateParamsToApply, itinerarySource: source || "main_map" });
  }
  showRouteBuilder() {
    this.setState({
      mode: "waypoints",
      showRouteBuilderSummary: false,
      showPanel: true,
    });
  }
  editStops() {
    this.setState({
      mode: "waypoints",
      routeBuilderView: "default",
      showRouteBuilderSummary: false,
      showPanel: true,
    });
  }
  handleRemoveWaypoint(stopToRemove: woosmap.map.GeoJSONFeature) {
    this.setState({ removingWaypointId: stopToRemove.id });
    setTimeout(() => {
      const { selectedWaypoints } = this.state;
      this.setState({
        selectedWaypoints: selectedWaypoints.filter(
          (stop) => stop.id !== stopToRemove.id,
        ),
        removingWaypointId: null,
      });
    }, 250);
  }
  onExitGateOpted() {
    const {
      showExitGates,
      selectedWaypoints,
      selectedDestination,
      selectedExitGate,
    } = this.state;
    const willShowExitGates = !showExitGates;
    this.routeBuilderWarning = getLocale().indoor.exit_gate_warning;
    if (!willShowExitGates) {
      //Remove exit gate as destination if any
      if (
        selectedDestination &&
        selectedDestination.properties &&
        (selectedDestination.properties as Record<string, any>)[
        "multi_nav_exit"
        ] === "yes"
      ) {
        const destinationToSet =
          selectedWaypoints[selectedWaypoints.length - 1];
        const updatedWaypoints = selectedWaypoints.slice(
          0,
          selectedWaypoints.length - 1,
        );
        this.setState({
          selectedWaypoints: updatedWaypoints,
          selectedDestination: destinationToSet,
          selectedFeature: destinationToSet,
          selectedExitGate: undefined,
        });
      }
    }
    this.setState({
      showExitGates: !showExitGates,
      showRouteBuilderWarning: !showExitGates,
    });
    setTimeout(() => {
      this.onHideWarningBanner();
    }, 3000);
  }
  onExitGateSelected(exitGate: woosmap.map.GeoJSONFeature) {
    const {
      selectedWaypoints,
      selectedDestination,
      selectedOrigin,
      selectedExitGate,
    } = this.state;
    if (selectedExitGate?.id === exitGate.id) {
      this.setState({
        selectedExitGate: undefined,
        selectedDestination: undefined,
      });
      return;
    }
    let newWaypoints = selectedWaypoints.filter(
      (stop) => (stop.properties as Record<string, any>)["entrance"] !== "yes",
    );

    const currentTotal =
      newWaypoints.length +
      (selectedOrigin ? 1 : 0) +
      (selectedDestination ? 1 : 0);

    const hasNonExitGateDestination =
      selectedDestination &&
      (selectedDestination.properties as Record<string, any>)["entrance"] !==
      "yes";

    if (
      selectedOrigin &&
      (selectedOrigin.id === exitGate.id)
    ) {
      this.routeBuilderWarning =
        getLocale().indoor.same_origin_destination_warning ||
        `Origin & Destination cannot be same`;

      this.setState({
        showRouteBuilderWarning: true,
        selectedExitGate: undefined,
      });
      setTimeout(() => this.onHideWarningBanner(), 3000);
      return;
    }

    // If at max limit with a destination set, replace destination with exit gate
    // Old destination gets unselected (not added to waypoints)
    if (
      currentTotal >= getConfig().map.MAX_WAYPOINTS_ALLOWED &&
      hasNonExitGateDestination
    ) {
      this.setState({
        selectedExitGate: exitGate,
        selectedWaypoints: newWaypoints,
        selectedDestination: exitGate,
      }, () => this.scrollToLastRouteElement());
      return;
    }

    // If no destination or destination is already an exit gate, check if we can add
    if (!hasNonExitGateDestination) {
      const totalAfterAdd = newWaypoints.length + (selectedOrigin ? 1 : 0) + 1; // +1 for new destination (exitGate)

      if (totalAfterAdd > getConfig().map.MAX_WAYPOINTS_ALLOWED) {
        this.routeBuilderWarning =
          getLocale().indoor.max_stop_limit_warning ||
          `Max stop limit exceeded`;

        this.setState({
          showRouteBuilderWarning: true,
          selectedExitGate: undefined,
        });
        setTimeout(() => this.onHideWarningBanner(), 3000);
        return;
      }
    } else {
      // Has a non-exit-gate destination and not at max, add it to waypoints
      newWaypoints = [...newWaypoints, selectedDestination];
    }

    this.setState({
      selectedExitGate: exitGate,
      selectedWaypoints: newWaypoints,
      selectedDestination: exitGate,
    }, () => this.scrollToLastRouteElement());
  }
  scrollToLastRouteElement() {
    const container = this.panelRef?.querySelector(".route-panel__content");
    if (container) {
      const lastChild = container.lastElementChild;
      if (lastChild) {
        lastChild.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }
  }
  onHideWarningBanner = () => {
    this.routeBuilderWarning = "";
    this.setState({ showRouteBuilderWarning: false });
  };
  stopExistsInRoute(stopToFind: woosmap.map.GeoJSONFeature): boolean {
    const { selectedWaypoints, selectedOrigin, selectedDestination } =
      this.state;
    const stopFound = selectedWaypoints.filter(
      (stop) => stop.id == stopToFind.id,
    );
    if (selectedOrigin && selectedOrigin.id == stopToFind.id) {
      stopFound.push(selectedOrigin);
    }
    if (selectedDestination && selectedDestination.id == stopToFind.id) {
      stopFound.push(selectedDestination);
    }
    return stopFound.length > 0 ? true : false;
  }
  onBuildRouteWithWaypoints() {
    this.setState({ isBuildingRoute: true });
    this.findRoute();
  }
  addCustomMarkersForWaypoints(results: woosmap.map.IndoorDirectionResult) {
    if (this.waypointsMarkers && this.waypointsMarkers.length > 0) {
      this.waypointsMarkers.map((marker) => {
        marker.setMap(null);
      });
    }
    this.waypointsMarkers = [];
    if (results && results.routes && results.routes.length > 0) {
      const legs = results.routes[0].legs;
      if (legs && legs.length > 0) {
        let strWaypoints = "";
        const { widgetMapMode } = this.state;
        let anchor = { x: 14, y: 14 };
        if (widgetMapMode === "favorites") {
          anchor = { x: 14, y: 7 };
        }
        legs
          .slice(0, -1)
          .forEach((leg: woosmap.map.IndoorDirectionLeg, index) => {
            const wpPosition = leg.end_location;
            const marker = new woosmap.map.Marker({
              position: new woosmap.map.LatLng(wpPosition.lat, wpPosition.lng),
              icon: {
                url: getConfig().urls.waypoints + `/stop_${index + 1}@3x.png`,
                scaledSize: {
                  height: 28,
                  width: 28,
                },
                anchor,
              },
            });
            this.waypointsMarkers.push(marker);
            marker.setMap(this.map);
          });
      }
    }
  }
  onTouchStart = (e: TouchEvent) => {
    e.stopPropagation();
    const content = this.panelRef?.querySelector(
      ".route-panel__content",
    ) as HTMLElement;

    if (content && content.scrollTop > 0) return;

    this.startY = e.touches[0].clientY;
    this.setState({ isDragging: true });
  };

  onTouchMove = (e: TouchEvent) => {
    e.stopPropagation();
    if (!this.panelRef || !this.state.isDragging) return;

    e.preventDefault();
    const currentY = e.touches[0].clientY;
    const deltaY = Math.max(0, currentY - this.startY);

    this.panelRef.style.transform = `translateY(${deltaY}px)`;
  };
  onTouchEnd = (e: TouchEvent) => {
    e.stopPropagation();
    if (!this.panelRef) return;

    const endY = e.changedTouches[0].clientY;
    const deltaY = endY - this.startY;

    this.panelRef.style.transform = "";
    this.setState({ isDragging: false });

    if (deltaY > 120) {
      this.setState({ showRouteBuilderSummary: true }); // collapse
    } else {
      this.setState({ showRouteBuilderSummary: false }); // expand
    }
  };
  cancelRouteBuilding() {
    this.clearRouteAndItinerary();
    this.clearStopMarkers();
    this.setState({
      selectedStop: undefined,
      searchedStops: [],
      showRouteBuilderSummary: false,
      selectedWaypoints: [],
      removingWaypointId: null,
      showExitGates: false,
      selectedExitGate: undefined,
      allExitGatesLoaded: false,
      allExitGates: [],
      isBuildingRoute: false,
      routeBuilderView: "default",
      isDragging: false,
      showRouteBuilderWarning: false,
      showPanel: false,
      mode: "default",
      selectedOrigin: undefined,
      selectedDestination: undefined,
      shopsMarkedForVisit: undefined,
      routeBuilderToastDismissed: false,
      itinerarySource: "",
    });
    this.controller.setSelectedWaypoints(null);
    if (this.controller.getWidgetMapMode() === "thematic") {
      const thematicDetails = document.querySelector(".thematic-details");
      (thematicDetails as any).style.display = "flex";
    }
  }
  clearStopMarkers() {
    if (this.waypointsMarkers && this.waypointsMarkers.length > 0) {
      this.waypointsMarkers.forEach((marker) => {
        marker.setMap(null);
      });
      this.waypointsMarkers = [];
    }
  }
  toggleShop = (shop: woosmap.map.GeoJSONFeature) => {
    this.setState((prev) => {
      const exists = prev.selectedWaypoints.some((f) => f.id === shop.id);
      if (exists) {
        return {
          selectedWaypoints: prev.selectedWaypoints.filter(
            (f) => f.id !== shop.id,
          ),
        };
      }

      // Calculate total stops INCLUDING origin and destination
      const currentTotal =
        prev.selectedWaypoints.length +
        (prev.selectedOrigin ? 1 : 0) +
        (prev.selectedDestination ? 1 : 0);

      if (currentTotal >= getConfig().map.MAX_WAYPOINTS_ALLOWED) {
        this.routeBuilderWarning =
          getLocale().indoor.max_stop_limit_warning ||
          `Max stop limit exceeded`;
        this.setState({ showRouteBuilderWarning: true });
        setTimeout(() => {
          this.onHideWarningBanner();
        }, 3000);
        return null;
      }

      return {
        selectedWaypoints: [...prev.selectedWaypoints, shop],
      };
    });
  };
  setFavShopAsOrigin = (shop: woosmap.map.GeoJSONFeature) => {
    const { selectedOrigin, selectedWaypoints, selectedDestination } =
      this.state;

    // Check if shop is already part of the route
    const isShopInWaypoints = selectedWaypoints.some((wp) => wp.id === shop.id);
    const isShopOrigin = selectedOrigin?.id === shop.id;
    const isShopDestination = selectedDestination?.id === shop.id;
    const isShopAlreadyInRoute =
      isShopInWaypoints || isShopOrigin || isShopDestination;

    // If shop is not already in route, check if adding it would exceed limit
    if (!isShopAlreadyInRoute) {
      const currentTotal =
        selectedWaypoints.length +
        (selectedOrigin ? 1 : 0) +
        (selectedDestination ? 1 : 0);

      if (currentTotal >= getConfig().map.MAX_WAYPOINTS_ALLOWED) {
        this.routeBuilderWarning =
          getLocale().indoor.max_stop_limit_warning ||
          `Max stop limit exceeded`;
        this.setState({ showRouteBuilderWarning: true });
        setTimeout(() => this.onHideWarningBanner(), 3000);
        return;
      }
    }

    // If shop is currently the destination, clear it
    const newDestination = isShopDestination ? undefined : selectedDestination;

    // Remove shop from waypoints if present
    let newWaypoints = selectedWaypoints.filter((wp) => wp.id !== shop.id);

    // Add old origin back to waypoints if it exists and is different
    if (selectedOrigin && selectedOrigin.id !== shop.id) {
      newWaypoints = [...newWaypoints, selectedOrigin];
    }

    this.setState({
      selectedOrigin: shop,
      selectedDestination: newDestination,
      selectedWaypoints: newWaypoints,
    });
  };
  setFavShopAsDestination = (shop: woosmap.map.GeoJSONFeature) => {
    const { selectedDestination, selectedWaypoints, selectedOrigin, selectedExitGate } =
      this.state;

    // Check if shop is already part of the route
    const isShopInWaypoints = selectedWaypoints.some((wp) => wp.id === shop.id);
    const isShopOrigin = selectedOrigin?.id === shop.id;
    const isShopDestination = selectedDestination?.id === shop.id;
    const isShopAlreadyInRoute =
      isShopInWaypoints || isShopOrigin || isShopDestination;

    // If shop is not already in route, check if adding it would exceed limit
    if (!isShopAlreadyInRoute) {
      const currentTotal =
        selectedWaypoints.length +
        (selectedOrigin ? 1 : 0) +
        (selectedDestination ? 1 : 0);

      if (currentTotal >= getConfig().map.MAX_WAYPOINTS_ALLOWED) {
        this.routeBuilderWarning =
          getLocale().indoor.max_stop_limit_warning ||
          `Max stop limit exceeded`;
        this.setState({ showRouteBuilderWarning: true });
        setTimeout(() => this.onHideWarningBanner(), 3000);
        return;
      }
    }

    // If shop is currently the origin, clear it
    const newOrigin = isShopOrigin ? undefined : selectedOrigin;

    // Remove shop from waypoints if present
    let newWaypoints = selectedWaypoints.filter((wp) => wp.id !== shop.id);

    // Add old destination back to waypoints if it exists, is different, and is not an exit gate
    const isOldDestinationExitGate = selectedExitGate && selectedDestination?.id === selectedExitGate.id;
    if (selectedDestination && selectedDestination.id !== shop.id && !isOldDestinationExitGate) {
      newWaypoints = [...newWaypoints, selectedDestination];
    }

    this.setState({
      selectedDestination: shop,
      selectedOrigin: newOrigin,
      selectedExitGate: undefined,
      selectedWaypoints: newWaypoints,
    }, () => this.scrollToLastRouteElement());

  };
  prepareShopsToVisitForRouteBuilding = (
    shops: woosmap.map.GeoJSONFeatureCollection,
  ) => {
    const { widgetMapMode, mode } = this.state;
    const filterShopsByStatus = shops?.features.filter(
      (feature) => (feature.properties as any)["visit_status"] === "tovisit",
    );
    if (filterShopsByStatus && filterShopsByStatus.length > 0) {
      this.shopsToVisit = {
        type: "FeatureCollection",
        features: filterShopsByStatus,
      };
    } else {
      this.shopsToVisit = undefined;
    }
    if (widgetMapMode === "favorites" && this.shopsToVisit) {
      const shopsCount = this.shopsToVisit.features.length;

      if (shopsCount < 3) {
        // Less than 3 shops: do not auto-add as waypoints
      } else if (shopsCount <= getConfig().map.MAX_WAYPOINTS_ALLOWED) {
        // All shops can be added as waypoints & pre-selected also, if they are less than 15
        this.setState({
          selectedWaypoints: this.shopsToVisit.features,
          selectAllFavorites: true,
        });
      } else if (shopsCount > 2) {
        // More than 2 shops: add all as waypoints (will be optimized) but do not pre-select, let user choose.
        this.setState({
          selectAllFavorites: false,
        });
      }
      //ONE MORE CONDITION if shops <= 2
      // TO DO:
      // If exactly 1 or 2 shops: don't auto-add as waypoints
      // User will set them as origin/destination manually
    }
  };
  renderHeader(): ComponentChildren {
    const { mode, showPanel } = this.state;
    const modeClassItinerary = `woosmap-panel__header woosmap-panel__header--itinerary desktop ${showPanel ? "left" : ""}`;
    const modeClassLevels = `woosmap-panel__header woosmap-panel__header--levels ${showPanel ? "left" : ""}`;

    const isMobileView = () => {
      let res = false;
      if (this.controller._renderer.getView() !== "desktop") {
        if (
          this.map &&
          (this.map as any)._container &&
          (this.map as any)._container.clientWidth < 768
        ) {
          res = true;
        }
      }
      return res;
    };
    if (mode === "itinerary") {
      return (
        <div className={modeClassItinerary} key="iti">
          <Icon className="featured-icon" icon="itinerary-filled" />
          <h1 className="title">{getLocale().indoor.Itinerary}</h1>
          <button
            className="btn btn--main btn--close"
            onClick={() => {
              this.setState({
                mode: "default",
                directionsResult: undefined,
                stepSelected: "",
                showPanel: false,
                selectedFeature: undefined,
              } as any);
              this.controller._renderer.setDirections(null);
              const { userLocation } = this.state;
              this.setState({
                selectedOrigin: userLocation,
                selectedDestination: undefined,
              });
              this.controller._renderer.unselectFeature();
              this.cancelRouteBuilding();
              /**
               * This event is fired when the user performs any widget specific activities.
               * @event IndoorWidget#indoor_widget_analytics
               * @property {string} event ITINERARY_MODE_EXITED.
               * @property {*} args
               */
              this.controller.trigger(this, "indoor_widget_analytics", [
                {
                  event: EVENTS.ITINERARY_MODE_EXITED,
                },
              ]);
            }}
          >
            <Icon icon="close" />
          </button>
        </div>
      );
    }
    if (mode === "level") {
      return (
        <div className={modeClassLevels} key="levels">
          <Icon className="featured-icon" icon="stairs-thick" />
          <h1 className="title">{getLocale().indoor.levels}</h1>
          <button
            className="btn  btn--main btn--close"
            onClick={() => this.setState({ mode: "default", showPanel: false })}
          >
            <Icon icon="close" />
          </button>
        </div>
      );
    }
    if (mode === "waypoints") {
      return null;
    }
    return (
      <>
        {this.state.widgetMapMode === "favorites" && isMobileView() && (
          <button
            className="woosmap-panel__customcta"
            disabled={this.state.isUserUnauthorized || (this.shopsToVisit?.features?.length ?? 0) < 3}
            onClick={this.showRouteBuilder}
          >
            {getLocale().indoor.optimized_route_cta_label}
          </button>
        )}
        <div className="woosmap-panel__header" key="auto">
          <Autocomplete
            src={(value: any) =>
              this.autocomplete(
                "searchedFeatures",
                "searchedFeaturesText",
                value,
              )
            }
            keys={["name"]}
            placeholder={getLocale().indoor.Search}
            onClose={() => this.onAutoCompleteClose("searchedFeaturesText")}
            callbackSelect={(value: any) => {
              let selectedFeature = null;
              const padding = { left: 0, bottom: 0, top: 0 };
              if (this.map && (this.map as any)._container) {
                const mapContainerWidth = (this.map as any)._container
                  .clientWidth;
                switch (this.controller._renderer.getView()) {
                  case "mobile":
                    padding.bottom = this.panelHeight;
                    break;
                  case "desktop":
                    padding.left = this.panelWidth;
                    break;
                  case "auto":
                    if (mapContainerWidth > 768) {
                      padding.left = this.panelWidth;
                    } else {
                      padding.bottom = this.panelHeight;
                    }
                    break;
                }
              }
              const venue = this.controller._renderer.getVenue();
              if (venue && value) {
                if (value.id > 0) {
                  new Promise((resolve) => {
                    const callback = (featuresCollection: any) => {
                      selectedFeature =
                        featuresCollection && featuresCollection.features[0];
                      resolve([
                        (this.controller._renderer as any).selectFeature(
                          selectedFeature,
                          padding,
                        ),
                        /**
                         * This event is fired when the user performs any widget specific activities.
                         * @event IndoorWidget#indoor_widget_analytics
                         * @property {string} event SEARCH_RESULT_ITEM_SELECTED.
                         * @property {*} args The GeoJSON feature selected by the user.
                         */
                        this.controller.trigger(
                          this,
                          "indoor_widget_analytics",
                          [
                            {
                              event: EVENTS.SEARCH_RESULT_SELECTED,
                              args: selectedFeature,
                            },
                          ],
                        ),
                      ]);
                    };
                    this.indoorService.search(
                      venue.venue_id,
                      "",
                      callback,
                      undefined,
                      value.id.toString(),
                    );
                  });
                } else {
                  const { userLocation } = this.state;
                  if (userLocation && value.id === -1) {
                    selectedFeature = userLocation;
                    this.controller.trigger(this, "indoor_widget_analytics", [
                      {
                        event: EVENTS.SEARCH_RESULT_SELECTED,
                        args: selectedFeature,
                      },
                    ]);
                  }
                }
              }
            }}
            disableAutoFocus={this.controller.getIsAutofocusDisabled()}
            isMobileView={isMobileView()}
          />
          <button
            className="btn btn--main btn--primary btn--itinerary"
            onClick={() =>
              this.setState({
                mode: "itinerary",
                showPanel: true,
                wheelChairEnabled:
                  this.controller.getDirectionsMode() === "wheelchair",
              })
            }
          >
            <Icon icon="itinerary-filled" />
          </button>
          <button
            className="btn  btn--main btn--secondary btn--levels"
            onClick={() => this.setState({ mode: "level", showPanel: true })}
          >
            <Icon icon="stairs-thick" />
          </button>
        </div>
        {this.state.widgetMapMode === "favorites" && isMobileView() &&
          !this.state.staticToastDismissed &&
          (this.state.isUserUnauthorized ||
            (this.shopsToVisit?.features?.length ?? 0) === 0) && (
            <div className="woosmap-panel__static-toast">
              <span className="woosmap-panel__static-toast__text">
                {this.state.isUserUnauthorized
                  ? getLocale().indoor.unauthorized_map_toast
                  : getLocale().indoor.empty_map_toast}
              </span>
              <button
                type="button"
                className="woosmap-panel__static-toast__close"
                aria-label="Dismiss"
                onClick={() => this.setState({ staticToastDismissed: true })}
              >
                <Icon icon="close" size={15} />
              </button>
            </div>
          )}
        {this.state.widgetMapMode === "favorites" && !isMobileView() && !this.state.showPanel && (
          <div className="woosmap-panel__route-card">
            <p className="woosmap-panel__route-card__desc">
              {this.state.isUserUnauthorized
                ? getLocale().indoor.unauthorised_favs
                : (this.shopsToVisit?.features?.length ?? 0) < 3
                  ? getLocale().indoor.fav_route_builder_min_stop_message
                  : getLocale().indoor.optimized_route_cta_desc}
            </p>
            <button
              className="woosmap-panel__route-card__cta"
              disabled={this.state.isUserUnauthorized || (this.shopsToVisit?.features?.length ?? 0) < 3}
              onClick={this.showRouteBuilder}
            >
              {`${getLocale().indoor.optimized_route_cta_button}`}
            </button>
          </div>
        )}
      </>
    );
  }

  renderItinerary(): ComponentChildren {
    const {
      mode,
      directionsResult,
      routeErrorMessage,
      stepSelected,
      isLoading,
      wheelChairEnabled,
      widgetMapMode,
      routeBuilderView
    } = this.state;
    const { selectedFeature, selectedOrigin, selectedDestination } = this.state;
    let defaultValue,
      defaultOrigin = "";
    let duration,
      distance,
      levels = "";
    let origin = "";
    let destination = "";
    let firstStopInIntinerary: woosmap.map.GeoJSONFeature | undefined;
    const routeSteps: any[] = [];

    const isMobileView = () => {
      let res = false;
      if (this.controller._renderer.getView() !== "desktop") {
        if (
          this.map &&
          (this.map as any)._container &&
          (this.map as any)._container.clientWidth < 768
        ) {
          res = true;
        }
      }
      return res;
    };

    if (mode !== "itinerary") {
      this.controller.setIsItineraryMode(false);
      return null;
    } else {
      this.controller.setIsItineraryMode(true);
      if (selectedFeature && selectedFeature !== selectedOrigin) {
        if ((selectedFeature.properties as any)[`name:${getLocaleLang()}`]) {
          defaultValue = `${(selectedFeature.properties as any)[`name:${getLocaleLang()}`]} ${getLocale().indoor["at Level"]} (${(selectedFeature.properties as any)["level"]})`;
        } else {
          defaultValue = `${(selectedFeature.properties as any)["name"]} ${getLocale().indoor["at Level"]} (${(selectedFeature.properties as any)["level"]})`;
        }
      }
      if (selectedOrigin) {
        origin = `${(selectedOrigin.properties as any as any)[`name:${getLocaleLang()}`] ? (selectedOrigin.properties as any)[`name:${getLocaleLang()}`] : (selectedOrigin.properties as any)["name"]} ${getLocale().indoor["at Level"]} (${(selectedOrigin.properties as any)["level"]})`;
        if ((selectedOrigin.properties as any)[`name:${getLocaleLang()}`]) {
          defaultOrigin = `${(selectedOrigin.properties as any)[`name:${getLocaleLang()}`]} ${getLocale().indoor["at Level"]} (${(selectedOrigin.properties as any)["level"]})`;
        } else {
          defaultOrigin = `${(selectedOrigin.properties as any)["name"]} ${getLocale().indoor["at Level"]} (${(selectedOrigin.properties as any)["level"]})`;
        }
      }
      if (selectedDestination) {
        destination = `${(selectedDestination.properties as any)[`name:${getLocaleLang()}`] ? (selectedDestination.properties as any)[`name:${getLocaleLang()}`] : (selectedDestination.properties as any)["name"]} ${getLocale().indoor["at Level"]} (${(selectedDestination.properties as any)["level"]})`;
      }
      if (defaultValue === undefined) {
        defaultValue = destination;
      }
      if (
        directionsResult &&
        directionsResult.routes &&
        directionsResult.routes.length > 0
      ) {
        const route = directionsResult.routes[0];
        if (route.legs && route.legs.length > 0) {
          duration =
            route.legs[0].duration.value > 60
              ? this.convertDurationToMins(
                Math.round(route.legs[0].duration.value),
              )
              : route.legs[0].duration.text;
          distance = route.legs[0].distance.text;
          const levelTransitonObject = this.composeLevelTransition(
            route.legs[0].start_location.level,
            route.legs[0].end_location.level,
          );
          if ((levelTransitonObject as any)["level"] > 0) {
            levels = `${(levelTransitonObject as any)["level"]} ${(levelTransitonObject as any)["content"]} ${(levelTransitonObject as any)["direction"]}`;
          }
          route.legs.forEach((leg) => {
            if (leg && leg.steps) {
              leg.steps.forEach((step) => {
                const feature = {
                  type: "Feature",
                  properties: {
                    id: `${route.legs.indexOf(leg)}_${leg.steps.indexOf(step)}`,
                    level: `${getLocale().indoor.Level} ${step.start_location.level}`,
                    floor: step.start_location.level,
                    instruction: step.instruction
                      ? step.instruction.summary
                      : null,
                    icon:
                      step.instruction && step.instruction.instruction_type
                        ? step.instruction.instruction_type
                        : "caret-down",
                  },
                  geometry: {
                    type: "LineString",
                    coordinates: step.polyline,
                  },
                };
                routeSteps.push(feature);
              });
            }
          });
          if (route.legs && route.legs.length > 1) {
            //Multi-navigation mode with waypoints
            const { selectedWaypoints } = this.state;
            const waypointId = (route.legs[0] as any).waypoint;
            firstStopInIntinerary = selectedWaypoints.find(
              (wp) => wp.id && wp.id.toString() === waypointId,
            );
          }
        }
      } else {
        const responsiveStatus = this.controller._renderer.getView();
        const map = this.map;
        if (map && (map as any)._container && selectedFeature) {
          const centrePoint = pointOnFeature(selectedFeature);
          const lat = centrePoint.geometry.coordinates[1];
          const lng = centrePoint.geometry.coordinates[0];
          const padding = { bottom: 0, left: 0 };
          let flyToOptions: any = { center: { lat, lng } };
          switch (responsiveStatus) {
            case "mobile":
              // eslint-disable-next-line max-len
              flyToOptions =
                map.getZoom() < 18
                  ? { ...flyToOptions, zoom: 18, padding }
                  : { ...flyToOptions, padding };
              break;
            case "desktop":
              if ((map as any)._container.clientWidth <= 768) {
                flyToOptions =
                  map.getZoom() < 18
                    ? { ...flyToOptions, zoom: 18 }
                    : flyToOptions;
              }
              break;
            default:
              if ((map as any)._container.clientWidth <= 768) {
                // eslint-disable-next-line max-len
                flyToOptions =
                  map.getZoom() < 18
                    ? { ...flyToOptions, zoom: 18, padding }
                    : { ...flyToOptions, padding };
              }
              break;
          }
          map.flyTo(flyToOptions);
        }
      }
    }
    const instructionsByLevel = this.groupInstructionsByLevel(routeSteps);
    const noResultClass = routeSteps.length > 0 ? "" : "no-result";
    const { showItineraryResults } = this.state;
    const itineraryResultClass = showItineraryResults ? "hide-results" : "";
    return (
      <>
        <div className="woosmap-panel__content__header desktop">
          <button
            className="btn btn--retract mobile"
            onClick={() => this.setState({ showPanel: false })}
          >
            <Icon size={17} icon="arrow-left" />
          </button>
          <div className="woosmap-panel__content__header__itinenary">
            <h2 className="text">
              {getLocale().indoor["Select a location and a destination"]}
            </h2>
          </div>
        </div>
        <div className="woosmap-panel__content__body woosmap-panel__content__body--itinerary">
          <div className="itinerary-build">
            <button
              className="btn btn--close mobile"
              onClick={() => {
                this.setState({
                  mode: "default",
                  directionsResult: null,
                  stepSelected: "",
                  showPanel: false,
                  selectedFeature: undefined,
                  selectedOrigin: undefined,
                });
                this.controller._renderer.setDirections(null);
                this.setState({
                  selectedOrigin: undefined,
                  selectedDestination: undefined,
                });
                (this.controller._renderer as any).selectFeature(undefined);
                if ((this.controller._renderer as any)._activeInfoWindow) {
                  (this.controller._renderer as any)._activeInfoWindow.close();
                }
                this.cancelRouteBuilding();
                /**
                 * This event is fired when the user performs any widget specific activities.
                 * @event IndoorWidget#indoor_widget_analytics
                 * @property {string} event ITINERARY_MODE_EXITED.
                 * @property {*} args
                 */
                this.controller.trigger(this, "indoor_widget_analytics", [
                  {
                    event: EVENTS.ITINERARY_MODE_EXITED,
                  },
                ]);
              }}
            >
              <Icon icon="arrow-left" />
            </button>
            <div className="icons">
              {/* {Revisit and validate with Serge} */}
              <Icon className="icon__origin" icon="position-filled" />
              <Icon className="icon__destination" icon="marker-filled" />
            </div>
            <div className="itinerary-build__fieldset">
              <div className="origin">
                {/* <Icon icon="position-filled" /> */}
                <Autocomplete
                  defaultValue={defaultOrigin}
                  src={(value: any) =>
                    this.autocomplete(
                      "searchedOrigins",
                      "searchedOriginsText",
                      value,
                    )
                  }
                  keys={["name"]}
                  placeholder={getLocale().indoor["Type a origin"]}
                  value={origin}
                  onClose={() =>
                    this.onAutoCompleteClose("searchedOriginsText")
                  }
                  callbackSelect={(value: any) => {
                    let selectedOrigin: any = null;
                    const venue = this.controller._renderer.getVenue();
                    if (venue && value) {
                      if (value.id > 0) {
                        new Promise((resolve) => {
                          const callback = (featuresCollection: any) => {
                            // eslint-disable-next-line max-len
                            if (
                              featuresCollection &&
                              selectedDestination &&
                              featuresCollection.features[0].id ===
                              selectedDestination.id
                            ) {
                              showToast(
                                getLocale().indoor
                                  .same_origin_destination_warning ||
                                `Origin & Destination cannot be same`,
                                "error",
                              );
                              const { selectedOrigin: prevOrigin } = this.state;
                              this.setState(
                                { selectedOrigin: undefined },
                                () => {
                                  this.setState({ selectedOrigin: prevOrigin });
                                },
                              );
                              return;
                            }
                            selectedOrigin =
                              featuresCollection &&
                              featuresCollection.features[0];
                            resolve(
                              this.setState({ selectedOrigin }, () => {
                                if (value) {
                                  this.findRoute();
                                } else {
                                  this.clearRouteAndItinerary();
                                }
                                this.controller.trigger(
                                  this,
                                  "indoor_widget_analytics",
                                  [
                                    {
                                      event: EVENTS.SEARCH_RESULT_SELECTED,
                                      args: selectedOrigin,
                                    },
                                  ],
                                );
                              }),
                            );
                          };
                          this.indoorService.search(
                            venue.venue_id,
                            "",
                            callback,
                            undefined,
                            value.id.toString(),
                          );
                        });
                      } else {
                        const { userLocation } = this.state;
                        if (userLocation && value.id === -1) {
                          selectedOrigin = userLocation;
                          this.setState({ selectedOrigin }, () => {
                            if (value) {
                              this.findRoute();
                            } else {
                              this.clearRouteAndItinerary();
                            }
                            this.controller.trigger(
                              this,
                              "indoor_widget_analytics",
                              [
                                {
                                  event: EVENTS.SEARCH_RESULT_SELECTED,
                                  args: selectedOrigin,
                                },
                              ],
                            );
                          });
                        }
                      }
                    }
                  }}
                  disableAutoFocus={this.controller.getIsAutofocusDisabled()}
                  isMobileView={isMobileView()}
                />
              </div>
              <div className="destination">
                {/* <Icon icon="marker-filled" /> */}
                <Autocomplete
                  defaultValue={defaultValue}
                  src={(value: any) =>
                    this.autocomplete(
                      "searchedDestinations",
                      "searchedDestinationsText",
                      value,
                    )
                  }
                  keys={["name"]}
                  placeholder={getLocale().indoor["Type a destination"]}
                  onClose={() =>
                    this.onAutoCompleteClose("searchedDestinationsText")
                  }
                  value={destination}
                  callbackSelect={(value: any) => {
                    let selectedDestination = null;
                    const venue = this.controller._renderer.getVenue();
                    if (venue && value) {
                      if (value.id > 0) {
                        new Promise((resolve) => {
                          const callback = (featuresCollection: any) => {
                            // eslint-disable-next-line max-len
                            selectedDestination =
                              featuresCollection &&
                              featuresCollection.features[0];
                            resolve(
                              this.setState({ selectedDestination }, () => {
                                if (value) {
                                  this.findRoute();
                                } else {
                                  this.clearRouteAndItinerary();
                                }
                              }),
                            );
                            this.controller.trigger(
                              this,
                              "indoor_widget_analytics",
                              [
                                {
                                  event: EVENTS.SEARCH_RESULT_SELECTED,
                                  args: selectedDestination,
                                },
                              ],
                            );
                          };
                          this.indoorService.search(
                            venue.venue_id,
                            "",
                            callback,
                            undefined,
                            value.id.toString(),
                          );
                        });
                      } else {
                        const { userLocation } = this.state;
                        if (userLocation && value.id === -1) {
                          selectedDestination = userLocation;
                          this.setState({ selectedDestination }, () => {
                            if (value) {
                              this.findRoute();
                            } else {
                              this.clearRouteAndItinerary();
                            }
                          });
                          this.controller.trigger(
                            this,
                            "indoor_widget_analytics",
                            [
                              {
                                event: EVENTS.SEARCH_RESULT_SELECTED,
                                args: selectedDestination,
                              },
                            ],
                          );
                        }
                      }
                    }
                  }}
                  disableAutoFocus={this.controller.getIsAutofocusDisabled()}
                  isMobileView={isMobileView()}
                />
              </div>
            </div>

            <button
              className="btn btn--square btn--mini btn--inverse"
              onClick={this.swapOriginAndDestination}
            >
              <Icon icon="inverse" />
            </button>
          </div>
          <div className="disability">
            <Icon className="disability__icon" icon="disability" />
            <div className="disability__switcher">
              <Switch
                isSmall
                id="wheelchair_switch"
                checked={wheelChairEnabled}
                onChange={this.onDisabilitySwitchChange}
                text={getLocale().indoor["Disability compliant"]}
              />
            </div>
            {selectedOrigin &&
              selectedDestination &&
              widgetMapMode !== 'favorites' &&
              widgetMapMode !== 'kiosk' && (
                routeBuilderView === "results" ? (
                  <div
                    className="disability__waypoints"
                    onClick={this.editStops}
                  >
                    {getLocale().indoor.edit_stops}
                  </div>
                ) : (
                  <div
                    className="disability__waypoints"
                    onClick={this.showRouteBuilder}
                  >
                    {getLocale().indoor.stop_cta}
                  </div>
                )
              )
            }
          </div>
          {isLoading ? (
            <div className="itinerary-loader"></div>
          ) : (
            <div
              className={`itinerary-results ${noResultClass} ${itineraryResultClass}`}
            >
              {duration && (
                <div className="itinerary-results__header">
                  {duration &&
                    distance &&
                    (levels !== "" ? (
                      <div className="title-container">
                        <h2 className="title">{`${duration} - ${distance} - ${levels} `}</h2>
                        <p className="subtitle">
                          {firstStopInIntinerary &&
                            `${getLocale().indoor.for} ${(firstStopInIntinerary.properties as any).name}`}
                        </p>
                      </div>
                    ) : (
                      <div className="title-container">
                        <h2 className="title">{`${duration} - ${distance}`}</h2>
                        <p className="subtitle">
                          {firstStopInIntinerary &&
                            `${getLocale().indoor.for} ${(firstStopInIntinerary.properties as any).name}`}
                        </p>
                      </div>
                    ))}
                  {this.controller.getNavigationMode() && (
                    <button
                      className="btn btn--primary btn--small btn--navigate"
                      onClick={() => this.launchNavigation()}
                    >
                      <span className="desktop">
                        {getLocale().indoor.Navigate}
                      </span>
                      <Icon
                        className="mobile"
                        size={18}
                        icon="itinerary-start"
                      />
                    </button>
                  )}
                  <button
                    className="btn btn--toggle-results mobile"
                    onClick={() =>
                      this.setState({
                        showItineraryResults: !showItineraryResults,
                      })
                    }
                  >
                    <Icon
                      size={17}
                      icon={showItineraryResults ? "caret-up" : "caret-down"}
                    />
                  </button>
                </div>
              )}
              <div className="itinerary-results__body">
                {routeSteps.length > 0 ? (
                  instructionsByLevel.map(({ level, steps }, index) => {
                    const instructions = steps;
                    return (
                      <>
                        <div className="itinerary-results__body__section">
                          <button
                            onClick={() => this.toggleSteps(index)}
                            key={`level_${level}`}
                            className="btn--at-level btn btn--transparent btn--fill"
                          >
                            <Icon
                              size={13}
                              icon={
                                (this.state.levelClicked as any)[index] ===
                                  false
                                  ? "arrow-right"
                                  : "caret-down"
                              }
                            />
                            <span>{`${getLocale().indoor.At} ${level}`}</span>
                          </button>

                          <ul
                            className={`${(this.state.levelClicked as any)[index] === false ? "itinerary-results__list--collapsed" : ""}`}
                          >
                            {instructions.map((step: any) => {
                              return (
                                <li
                                  className={`${stepSelected === step.properties["id"] ? "itinerary-results__highlightedStep" : ""}`}
                                  key={step.properties["id"]}
                                  onClick={() => {
                                    this.highlightStep(step);
                                  }}
                                >
                                  <Icon
                                    size={13}
                                    icon={step.properties["icon"]}
                                  />
                                  <span>{step.properties["instruction"]}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </>
                    );
                  })
                ) : (
                  <div className="itinerary-noResults">{routeErrorMessage}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  renderLevel(): ComponentChildren {
    const { mode, allFeaturesLoaded, allFeatures } = this.state;
    const venue = this.controller._renderer.getVenue();
    if (mode !== "level" || !venue) {
      return null;
    }
    const padding = { left: 0, bottom: 0, top: 0 };
    if (this.map && (this.map as any)._container) {
      const mapContainerWidth = (this.map as any)._container.clientWidth;
      switch (this.controller._renderer.getView()) {
        case "mobile":
          padding.bottom = this.panelHeight;
          break;
        case "desktop":
          padding.left = this.panelWidth;
          break;
        case "auto":
          if (mapContainerWidth > 768) {
            padding.left = this.panelWidth;
          } else {
            padding.bottom = this.panelHeight;
          }
          break;
      }
    }

    return (
      <>
        <div className="woosmap-panel__content__header">
          <div className="tab">
            {/* <div className="tab__wrapper">
                            <button
                                className={`btn btn--tab ${activeTab === 'level' ? 'active' : ''}`}
                                onClick={() => this.setState({activeTab: 'level'})}
                            >
                                {this.translator.translate({text:'Levels', context: 'indoor'})}
                            </button>
                            <button
                                className={`btn btn--tab ${activeTab === 'shop' ? 'active' : ''}`}
                                onClick={() => this.setState({activeTab: 'shop'})}
                            >
                                {this.translator.translate({text:'Shops', context: 'indoor'})}
                            </button>
                        </div>*/}
          </div>
        </div>
        <div className="woosmap-panel__content__body woosmap-panel__content__body--levels">
          <div className="woosmap-panel__content__body__actions">
            <button
              className="btn btn--retract mobile"
              onClick={() => this.setState({ showPanel: false })}
            >
              <Icon size={17} icon="arrow-left" />
            </button>
          </div>
          {!allFeaturesLoaded ? (
            <div className="loading">{"Loading..."}</div>
          ) : null}
          {allFeaturesLoaded && (
            <>
              {venue.levels.map((level: any) => {
                const levelFeatures = allFeatures.filter((feature) => {
                  return (
                    (feature.properties as any).level === level.level.toString()
                  );
                });
                if (levelFeatures.length === 0) {
                  return null;
                }
                return (
                  <div className="card-container" key={level.level}>
                    <h2 className="title">
                      {level.name ||
                        `${getLocale().indoor.Level} ${level.level}`}
                    </h2>
                    <ul>
                      {levelFeatures.map((feat) => (
                        <li
                          key={feat.id}
                          onClick={() => {
                            (this.controller._renderer as any).selectFeature(
                              feat,
                              padding,
                            );
                            this.setState({
                              selectedDestination: feat,
                              mode: "default",
                            });
                            /**
                             * This event is fired when the user performs any widget specific activities.
                             * @event IndoorWidget#indoor_widget_analytics
                             * @property {string} event POI_SELECTED.
                             * @property {*} args The GeoJSON feature selected by the user from the list of POIs.
                             */
                            this.controller.trigger(
                              this,
                              "indoor_widget_analytics",
                              [{ event: EVENTS.SELECT_POI, args: feat }],
                            );
                          }}
                        >
                          <div className="card">
                            <div className="card__body">
                              <h3 className="shop-name">
                                {(feat.properties as any)[
                                  `name:${getLocaleLang()}`
                                ]
                                  ? (feat.properties as any)[
                                  `name:${getLocaleLang()}`
                                  ]
                                  : (feat.properties as any)["name"]}
                              </h3>
                            </div>
                            <button
                              className="btn btn--transparent"
                              onClick={(e) => {
                                e.stopPropagation();
                                (
                                  this.controller._renderer as any
                                ).selectFeature(feat, padding);
                                this.setState({ selectedDestination: feat });
                                this.setState({
                                  mode: "itinerary",
                                  showPanel: true,
                                });
                              }}
                            >
                              <Icon icon="itinerary-filled" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </>
    );
  }

  renderMall(): ComponentChildren {
    const { mode, selectedFeature } = this.state;
    if (mode !== "default") {
      return null;
    }
    let name: any,
      level: any,
      desc: any,
      phone: any,
      website: any,
      housenumber: any,
      street: any,
      postCode: any,
      city: any,
      coverImage: any,
      logo: any,
      osmHours: any,
      nextSlots: any[] = [];
    if (selectedFeature && selectedFeature.id !== -1) {
      const props = selectedFeature.properties as any;
      if (props[`name:${getLocaleLang()}`]) {
        name = props[`name:${getLocaleLang()}`];
      } else {
        name = props["name"];
      }
      level = props["level"];
      if (props[`description:${getLocaleLang().toLowerCase()}`]) {
        desc = props[`description:${getLocaleLang().toLowerCase()}`];
      } else {
        desc = props["description"];
      }
      if (props[`contact:phone:${getLocaleLang()}`]) {
        phone = props[`contact:phone:${getLocaleLang()}`];
      } else if (props["contact:phone"]) {
        phone = props["contact:phone"];
      }
      if (props[`contact:website:${getLocaleLang().toLowerCase()}`]) {
        website = props[`contact:website:${getLocaleLang().toLowerCase()}`];
      } else if (props["contact:website"]) {
        website = props["contact:website"];
      }
      if (props["addr:housenumber"]) {
        housenumber = props["addr:housenumber"];
      }
      if (props["addr:street"]) {
        street = props["addr:street"];
      }
      if (props["addr:postcode"]) {
        postCode = props["addr:postcode"];
      }
      if (props["addr:city"]) {
        city = props["addr:city"];
      }
      if (props[`woosmap:cover:${getLocaleLang()}`]) {
        coverImage = props[`woosmap:cover:${getLocaleLang()}`];
      } else if (props["woosmap:cover"]) {
        coverImage = props["woosmap:cover"];
      } else {
        coverImage = "https://sdk.woosmap.com/map/assets/default-img.png";
      }
      if (props[`woosmap:logo:${getLocaleLang()}`]) {
        logo = props[`woosmap:logo:${getLocaleLang()}`];
      } else if (props["woosmap:logo"]) {
        logo = props["woosmap:logo"];
      } else {
        logo = "https://sdk.woosmap.com/map/assets/default-logo.png";
      }
      if (
        props["opening_hours"] &&
        typeof props["opening_hours"] === "string"
      ) {
        osmHours = new OSMOpeningHours(props["opening_hours"]);
        if (osmHours && osmHours.opening_hours) {
          nextSlots = osmHours.getNextSlot(7);
        }
      }
    }
    const coverImageStyle = coverImage
      ? `background-image: url(${coverImage})`
      : `background-image: url(https://sdk.woosmap.com/map/assets/default-img.png)`;
    const logoImageStyle = logo
      ? `background-image: url(${logo})`
      : `background-image: url(https://sdk.woosmap.com/map/assets/default-logo.png)`;

    return (
      <>
        <div
          className="woosmap-panel__content__header woosmap-panel__content__header--featured-img"
          style={coverImageStyle}
        >
          <div className="logo" style={logoImageStyle} />
          <button
            className="btn btn--retract mobile"
            onClick={() => this.setState({ showPanel: false })}
          >
            <Icon size={17} icon="arrow-left" />
          </button>
          <button
            onClick={() =>
              this.setState({
                mode: "itinerary",
                showPanel: true,
                wheelChairEnabled:
                  this.controller.getDirectionsMode() === "wheelchair",
              })
            }
            className="btn btn--round btn--primary btn--itinerary"
          >
            <Icon icon="itinerary-filled" />
          </button>
        </div>
        <div className="woosmap-panel__content__body">
          <div className="woosmap-panel__content__body__title-wrapper">
            <h2 className="title">{name}</h2>
            <div className="level-info">
              <Icon size={16} icon="stairs-thick" />
              <span>
                {getLocale().indoor.Level} {level}
              </span>
            </div>
          </div>

          {desc && <p className="desc">{desc}</p>}

          <div className="date-selector">
            <div className="year" id="year1"></div>
            <div className="month" id="month1"></div>
            <div className="day" id="day1"></div>
          </div>
          {housenumber ||
            street ||
            postCode ||
            (city && (
              <p className="woosmap-panel__content__body__info">
                <Icon icon="marker" />
                <span>{housenumber}</span> <br />
                <span>{street}</span>
                <span>{postCode}</span> <br />
                <span>{city}</span>
              </p>
            ))}

          {website && (
            <p
              className="woosmap-panel__content__body__info"
              onClick={() => this.onWebsiteClicked(website)}
            >
              <a
                className="woosmap-panel__content__body__info__link"
                title={website}
                href={website}
                target="_blank"
                rel="noreferrer"
              >
                <Icon icon="world" />
                <span className="woosmap-panel__content__body__info__link__label">
                  {website}
                </span>
              </a>
            </p>
          )}
          {phone && (
            <p
              className="woosmap-panel__content__body__info"
              onClick={() => this.onPhoneClicked(phone)}
            >
              <Icon icon="phone" />
              <span> {phone}</span>
            </p>
          )}
          {/* Co-ordinate with Serge for UI */}
          {osmHours && osmHours.opening_hours && (
            <>
              <button
                className="btn--opening-hours btn btn--transparent"
                onClick={() => this.toggleOpeningHrs()}
              >
                {
                  <Icon
                    icon="clock"
                    className={`opening-hours-icon ${osmHours.getState() ? "opened" : "closed"}`}
                  />
                }
                {osmHours.getState() ? (
                  <span className="opened">{getLocale().indoor.Open}</span>
                ) : (
                  <span className="closed">{getLocale().indoor.Closed}</span>
                )}
                {osmHours.getState() ? (
                  osmHours.getNextChange() !== "" ? (
                    <span>
                      {getLocale().indoor["Closes at"]}{" "}
                      {osmHours.getNextChange()}
                    </span>
                  ) : (
                    <span>24 hours</span>
                  )
                ) : (
                  <span>
                    {`${getLocale().indoor.Opens} ${osmHours.getNextChangeDay()} ${getLocale().indoor.At}`}{" "}
                    {osmHours.getNextChange()}
                  </span>
                )}
                {nextSlots.length > 0 && (
                  <Icon
                    size={13}
                    icon={
                      this.state.openingHoursClicked === false
                        ? "caret-down"
                        : "caret-up"
                    }
                  />
                )}
              </button>
              <div
                className={`${this.state.openingHoursClicked === false ? "woosmap-panel__content__body--openinghours--collapsed" : "woosmap-panel__content__body--openinghours"}`}
              >
                <ul>
                  {nextSlots.map((slot: any, index: number) => {
                    return (
                      <li key={index}>
                        <span
                          className={`dayname ${slot.isSpecial ? "special" : index === 0 ? "current" : ""}`}
                        >
                          {slot.day}
                        </span>
                        <ul>
                          {slot.slots.map((timeRange: any, i: number) => (
                            <li
                              key={i}
                              className={`${slot.isSpecial ? "special" : index === 0 ? "current" : ""}`}
                            >
                              {timeRange.time}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  renderPanelContent(): ComponentChildren {
    const {
      showPanel,
      selectedFeature,
      mode,
      showRouteBuilderSummary,
      widgetMapMode,
    } = this.state;
    const modeClass = `woosmap-panel__content  ${showRouteBuilderSummary ? `${mode}--summary` : mode} ${showPanel ? "left" : ""}`;

    return (
      <>
        {!showPanel && (selectedFeature || mode !== "default") && (
          <button
            className={
              widgetMapMode === "favorites"
                ? "btn btn--expand-fav"
                : "btn btn--expand"
            }
            onClick={() => this.setState({ showPanel: true })}
          >
            <Icon size={17} icon="arrow-right" />
          </button>
        )}
        {showPanel && (
          <button
            className="btn btn--retract desktop"
            onClick={() => this.setState({ showPanel: false })}
          >
            <Icon size={17} icon="arrow-left" />
          </button>
        )}
        <div className={modeClass}>
          {this.renderMall()}
          {this.renderLevel()}
          {this.renderItinerary()}
          {this.renderRouteBuilder()}
        </div>
      </>
    );
  }
  /**
   * Renders the route builder panel with different views based on the current state.
   *
   * @returns {ComponentChildren} The rendered route builder panel component, or null if not in 'waypoints' mode
   *
   * @description
   * This method renders a comprehensive route building interface with the following features:
   * - Collapsed summary view showing origin, destination, and step count
   * - Expanded default view with origin, waypoints, and destination inputs
   * - Results view displaying optimized route with duration, distance, and stops
   * - Favorites view for selecting exhibitors to visit
   * - Search functionality for adding waypoints
   * - Exit gate selection toggle
   * - Mobile view detection and responsive behavior
   *
   * The component displays different content based on:
   * - `mode`: Only renders when mode is 'waypoints'
   * - `showRouteBuilderSummary`: Toggles between collapsed and expanded states
   * - `routeBuilderView`: Controls which view is shown ('default', 'results', or favorites)
   * - `isBuildingRoute`: Shows loading state when route is being calculated
   * - `getWidgetMapMode()`: Determines if showing general or favorites route builder
   *
   * @note
   * The primary action button is disabled when either `selectedOrigin` or `selectedDestination` is undefined.
   * The button will be disabled if BOTH are undefined.
   */
  renderRouteBuilder(): ComponentChildren {
    const { mode, showExitGates, showRouteBuilderSummary } = this.state;
    const {
      selectedFeature,
      selectedOrigin,
      selectedDestination,
      selectedWaypoints,
      directionsResult,
    } = this.state;
    let defaultValue,
      defaultOrigin = "";
    let duration: number = 0;
    let distance: number = 0;
    let origin = "";
    let destination = "";
    let durationMetric = "";
    let totalStops = 0;
    let selectAllFavs = false;
    let pav = "";
    const routeStops: any[] = [];
    if (selectedOrigin) {
      origin = `${(selectedOrigin.properties as any as any)[
        `
woosmap:shortname`
      ]
        ? (selectedOrigin.properties as any)[
        `
woosmap:shortname`
        ]
        : (selectedOrigin.properties as any)["name"]
        }`;
      if (
        (selectedOrigin.properties as any)[
        `
woosmap:shortname`
        ]
      ) {
        defaultOrigin = `${(selectedOrigin.properties as any)[
          `
woosmap:shortname`
        ]
          })`;
      } else {
        defaultOrigin = `${(selectedOrigin.properties as any)["name"]})`;
      }
      const originProps = selectedOrigin.properties as any;
      const originBuilding = originProps["Building"] ? displayBuilding(originProps["Building"]) : "";
      const originFloorLabel = originProps["Floorplan_Label"] || "";
      const originLevel = originProps["level"];
      pav = originBuilding
        ? `Pav ${originBuilding} ${originFloorLabel} ${getLocale().messages.level} ${originLevel}`
        : "";
    }
    if (selectedDestination) {
      const destProps = selectedDestination.properties as any;
      destination = destProps[`woosmap:shortname`]
        ? destProps[`woosmap:shortname`]
        : (destProps[`name:${getLocaleLang().toLowerCase()}`] || destProps["name"]);
    }
    if (
      directionsResult &&
      directionsResult.routes &&
      directionsResult.routes.length > 0
    ) {
      const route = directionsResult.routes[0];
      if (route.legs && route.legs.length > 0) {
        let waypointids: string = "";
        totalStops = route.legs.length + 1;
        route.legs.forEach((leg) => {
          duration = duration + leg.duration.value;
          distance = Math.round(distance + leg.distance.value);
          //@ts-ignore
          waypointids = waypointids.concat(
            leg.waypoint ? leg.waypoint + "," : "",
          );
        });
        const oldDuration = duration;
        duration = duration > 60 ? Math.round(duration / 60) : duration;
        durationMetric = oldDuration > 60 ? `mins` : `secs`;
        route.legs.slice(0).forEach((leg, index) => {
          const waypointId = (leg as any).waypoint;
          const foundWaypoint = selectedWaypoints.find(
            (wp) => wp.id && wp.id.toString() === waypointId,
          );
          const waypointName = foundWaypoint
            ? ((foundWaypoint.properties as any)[`name:${getLocaleLang().toLowerCase()}`]
              || (foundWaypoint.properties as any)["name"])
            : "";
          const waypointBuilding = foundWaypoint
            ? displayBuilding((foundWaypoint.properties as any)["Building"] || "")
            : "";
          const stop = {
            type: "Feature",
            properties: {
              id: waypointId,
              name: waypointName,
              building: waypointBuilding,
              duration:
                leg.duration.value > 60
                  ? `${Math.round(leg.duration.value / 60)} mins`
                  : `${leg.duration.value} secs`,
              distance: leg.distance.text,
            },
          };
          routeStops.push(stop);
        });
      }
    }

    const isMobileView = () => {
      let res = false;
      if (this.controller._renderer.getView() !== "desktop") {
        if (
          this.map &&
          (this.map as any)._container &&
          (this.map as any)._container.clientWidth < 768
        ) {
          res = true;
        }
      }
      return res;
    };
    if (mode !== "waypoints") {
      return null;
    }
    if (
      mode === "waypoints" &&
      this.controller.getWidgetMapMode() === "thematic" &&
      isMobileView()
    ) {
      const thematicDetails = document.querySelector(".thematic-details");
      if (thematicDetails) {
        (thematicDetails as any).style.display = "none";
      }
    }
    const buildFavoriteShopList = () => {
      const { selectedOrigin } = this.state;

      return this.shopsToVisit?.features.filter(
        (shop) =>
          shop.id !== selectedOrigin?.id && shop.id !== selectedDestination?.id,
      );
    };

    return (
      <div
        //@ts-ignore
        ref={(el) => (this.panelRef = el ?? undefined)}
        className={`route-panel ${showRouteBuilderSummary ? "route-panel--collapsed" : ""
          }`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      // onTouchStart={this.onTouchStart}
      // onTouchMove={this.onTouchMove}
      // onTouchEnd={this.onTouchEnd}
      >
        {/* Grab handle */}
        <div className="route-panel__handle" />
        {/* Header */}
        {showRouteBuilderSummary ? (
          /* -------- Collapsed summary -------- */
          <div className="route-panel__summary">
            <div className="route-panel__summary-row">
              <div className="route-panel__summary-text">
                <div className="route-panel__summary-title">
                  {`${getLocale().indoor.from} ${origin}`}
                </div>
                <div className="route-panel__summary-subtitle">
                  {`${getLocale().indoor.to} ${destination} - ${selectedWaypoints.length + (selectedOrigin && selectedDestination ? 2 : selectedOrigin || selectedDestination ? 1 : 0)} ${getLocale().indoor.steps}`}
                </div>
              </div>
              <button
                className="route-panel__summary-action"
                onClick={() =>
                  this.setState({ showRouteBuilderSummary: false })
                }
              >
                <Icon icon="plus" />
              </button>
            </div>
          </div>
        ) : (
          <div className="route-panel__header">
            <div className="route-panel__header-row">
              {isMobileView() ? (
                <>
                  <div className="route-panel__title">
                    {getLocale().indoor.route_panel_title}
                  </div>

                  <button
                    className="route-panel__close"
                    onClick={() =>
                      this.setState({ showRouteBuilderSummary: true })
                    }
                  >
                    <span className="route-panel__close-icon" />
                  </button>
                </>
              ) : (
                <>
                  <Icon className="featured-icon" icon="itinerary-filled" />
                  <div className="route-panel__title">
                    {getLocale().indoor.Itinerary}
                  </div>
                  <button
                    className="route-panel__close"
                    onClick={() => {
                      this.setState({
                        mode: "default",
                        directionsResult: undefined,
                        stepSelected: "",
                        showPanel: false,
                        selectedFeature: undefined,
                      } as any);
                      this.controller._renderer.setDirections(null);
                      const { userLocation } = this.state;
                      this.setState({
                        selectedOrigin: userLocation,
                        selectedDestination: undefined,
                      });
                      this.controller._renderer.unselectFeature();
                      this.cancelRouteBuilding();
                    }}
                  >
                    <Icon icon="close" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        {this.state.isBuildingRoute && !showRouteBuilderSummary ? (
          <div className="route-panel__loader">
            <div className="route-panel__spinner" />

            <div className="route-panel__loader-text">
              <h3>{getLocale().indoor.route_loader_header}</h3>
              <p>{getLocale().indoor.route_loader_title}</p>
            </div>
          </div>
        ) : (
          /* Content */
          <>
            {!showRouteBuilderSummary &&
              this.state.routeBuilderView === "default" &&
              (this.controller.getWidgetMapMode() === "general" ||
                this.controller.getWidgetMapMode() === "thematic") && (
                <div className="route-panel__content">
                  {/* Starting point */}
                  <div className="route-field">
                    <div className="route-field__label">
                      {getLocale().indoor.route_field_origin_label}
                    </div>

                    <div className="route-field__input route-field__input--primary">
                      <div className="route-field__left">
                        <Icon className="icon__origin" icon="position-filled" />
                        <div className="route-field__text">
                          <div className="route-field__title">{origin}</div>
                          <div className="route-field__subtitle">{pav}</div>
                        </div>
                        <button
                          className="route-field__edit"
                          type="button"
                          aria-label="Edit origin"
                          onClick={() => {
                            this.setState({ mode: "itinerary" });
                          }}
                        >
                          <svg
                            className="route-field__edit-icon"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M5.40001 18.6H6.67501L16.05 9.225L14.775 7.95L5.40001 17.325V18.6ZM3.60001 20.4V16.575L17.325 2.85L21.15 6.675L7.42501 20.4H3.60001ZM15.4013 8.59875L14.775 7.95L16.05 9.225L15.4013 8.59875Z"
                              fill="var(--Color-Icon-Secondary, #333333)"
                            />
                          </svg>
                        </button>
                      </div>

                      <span className="route-field__action" />
                    </div>
                  </div>
                  {selectedWaypoints.map(
                    (waypoint: woosmap.map.GeoJSONFeature, index: number) => (
                      <div
                        className={`route-stop${this.state.removingWaypointId === waypoint.id ? " route-stop--removing" : ""}`}
                        key={`wp-${waypoint.id}`}
                      >
                        <div className="route-stop__content">
                          <div className="route-stop__badge">
                            <span>{index + 1}</span>
                          </div>

                          <div className="route-stop__text">
                            <div className="route-stop__title">
                              {(waypoint.properties as any)[`name:${getLocaleLang().toLowerCase()}`]
                                || (waypoint.properties as any)["name"]}
                            </div>
                            <div className="route-stop__subtitle">
                              {displayBuilding((waypoint.properties as any)["Building"] || "")}
                            </div>
                          </div>
                        </div>

                        <button
                          className="route-stop__remove"
                          onClick={() => this.handleRemoveWaypoint(waypoint)}
                        >
                          <Icon icon="close" />
                        </button>
                      </div>
                    ),
                  )}

                  {/* Destination */}
                  <div className="route-field">
                    <div className="route-field__label">
                      {getLocale().indoor.route_field_des_label}
                    </div>

                    <div className="route-field__input route-field__input--error">
                      <div className="route-field__left">
                        <Icon
                          className="icon__destination"
                          icon="marker-filled"
                        />
                        <div className="route-field__text">
                          <div className="route-field__title">
                            {destination}
                          </div>
                          <div className="route-field__subtitle">
                            {(() => {
                              const destProps = selectedDestination?.properties as any;
                              const destBuilding = destProps?.["Building"];
                              return destBuilding
                                ? `Pav ${displayBuilding(destBuilding)} ${destProps["Floorplan_Label"] || ""} ${getLocale().messages.level} ${destProps["level"]}`
                                : "";
                            })()}
                          </div>
                        </div>
                        <button
                          className="route-field__edit"
                          type="button"
                          aria-label="Edit origin"
                          onClick={() => {
                            this.setState({ mode: "itinerary" });
                          }}
                        >
                          <svg
                            className="route-field__edit-icon"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M5.40001 18.6H6.67501L16.05 9.225L14.775 7.95L5.40001 17.325V18.6ZM3.60001 20.4V16.575L17.325 2.85L21.15 6.675L7.42501 20.4H3.60001ZM15.4013 8.59875L14.775 7.95L16.05 9.225L15.4013 8.59875Z"
                              fill="var(--Color-Icon-Secondary, #333333)"
                            />
                          </svg>
                        </button>
                      </div>

                      <span className="route-field__action" />
                    </div>
                  </div>
                </div>
              )}
            {!showRouteBuilderSummary &&
              this.state.routeBuilderView === "results" && (
                <>
                  {/* CONTENT */}
                  <div className="route-panel__content route-result">
                    {/* Optimized summary */}
                    <div className="route-result__summary">
                      <div className="route-result__summary-header">
                        <h3>{getLocale().indoor.results_summary_header}</h3>
                        <div>
                          <button
                            className="route-result__edit"
                            onClick={() => {
                              this.setState({ routeBuilderView: "default" });
                            }}
                          >
                            <Icon icon="edit" size={15} />
                            {getLocale().indoor.edit}
                          </button>
                        </div>
                      </div>
                      <div className="route-result-stats">
                        <div className="route-stats">
                          <Icon icon="time" />
                          <div className="route-stats__text">
                            <div className="route-stats__value">{duration}</div>
                            <div className="route-stats__label">
                              {durationMetric}
                            </div>
                          </div>
                        </div>
                        <div className="route-stats">
                          <Icon icon="distance" />
                          <div className="route-stats__text">
                            <div className="route-stats__value">{distance}</div>
                            <div className="route-stats__label">
                              {getLocale().indoor.meters}
                            </div>
                          </div>
                        </div>

                        <div className="route-stats">
                          <Icon icon="stops" />
                          <div className="route-stats__text">
                            <div className="route-stats__value">
                              {totalStops}
                            </div>
                            <div className="route-stats__label">
                              {getLocale().indoor.steps}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Route list */}
                    <div className="route-result__list">
                      {/* Example: Origin */}
                      <div className="route-stop route-stop--origin">
                        <Icon className="icon__origin" icon="position-filled" />
                        <div style={{ minWidth: "0" }}>
                          <div className="route-stop__title">{origin}</div>
                          <div className="route-stop__subtitle">
                            {displayBuilding((selectedOrigin?.properties as any)["Building"] || "")}
                          </div>
                        </div>
                      </div>

                      {/* Example: Leg */}
                      {routeStops.map((stop, index) => (
                        <div>
                          <div className="route-leg">
                            <Icon icon="down-arrow" />
                            <span>{`${stop.properties["duration"]} - ${stop.properties["distance"]}`}</span>
                          </div>
                          {/* Example: Stop */}
                          {index < routeStops.length - 1 && (
                            <div className="route-stop">
                              <div className="route-stop__index">
                                {index + 1}
                              </div>
                              <div className="route-stop__details">
                                <div className="route-stop__title">
                                  {stop.properties["name"]}
                                </div>
                                <div className="route-stop__subtitle">
                                  {stop.properties["building"]}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="route-stop route-stop--destination">
                        <Icon
                          className="icon__destination"
                          icon="marker-filled"
                        />
                        <div style={{ minWidth: "0" }}>
                          <div className="route-stop__title">{destination}</div>
                          <div className="route-stop__subtitle">
                            {displayBuilding((selectedDestination?.properties as any)["Building"] || "")}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            {!showRouteBuilderSummary &&
              this.state.routeBuilderView === "default" &&
              this.controller.getWidgetMapMode() === "favorites" && (
                <>
                  <div className="route-panel__content route-favorites">
                    <div className="route-favorites__intro">
                      <h3 className="route-favorites__title">
                        {getLocale().indoor.fav_route_builder_heading}
                      </h3>
                      <p className="route-favorites__subtitle">
                        {getLocale().indoor.fav_route_builder_sub_heading}
                      </p>
                    </div>
                    <div className="route-favorites__count">
                      {`${getLocale().indoor.fav_route_builder_selected_exhibitors} ${selectedWaypoints.length + (selectedOrigin && selectedDestination ? 2 : selectedOrigin || selectedDestination ? 1 : 0)}/${getConfig().map.MAX_WAYPOINTS_ALLOWED}`}
                    </div>
                    {!this.state.routeBuilderToastDismissed &&
                      !(selectedOrigin && selectedDestination) && (
                        <div className="route-favorites__toast">
                          <span className="route-favorites__toast-text">
                            {getLocale().indoor.route_builder_toast}
                          </span>
                          <button
                            type="button"
                            className="route-favorites__toast-close"
                            aria-label="Dismiss"
                            onClick={() =>
                              this.setState({ routeBuilderToastDismissed: true })
                            }
                          >
                            <Icon icon="close" size={15} />
                          </button>
                        </div>
                      )}
                    {this.state.selectedOrigin && (
                      <div className="route-field__input route-field__input--primary">
                        <div className="route-field__left">
                          <Icon
                            className="icon__origin"
                            icon="position-filled"
                          />
                          <div className="route-field__text">
                            <div className="route-field__title">{origin}</div>
                            <div className="route-field__subtitle">
                              {displayBuilding((selectedOrigin?.properties as any)["Building"] || "")}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Favorite shops List */}
                    <div className="route-favorites__list">
                      {buildFavoriteShopList()?.map((shop) => (
                        <div key={shop.id} className="route-favorites__item route-favorites__item--selected">
                          <div className="route-favorites__item-left">
                            <label className="route-favorites__radio">
                              <input
                                type="checkbox"
                                checked={
                                  selectedWaypoints.findIndex(
                                    (wp) => wp.id === shop.id,
                                  ) !== -1
                                }
                                onChange={() => this.toggleShop(shop)}
                              />
                              <span className="route-favorites__radio-icon" />
                            </label>

                            <div className="route-favorites__item-text">
                              <div className="route-favorites__item-title">
                                {(shop.properties as any)["name"]}
                              </div>
                              <div className="route-favorites__meta">
                                <div className="route-favorites__item-subtitle">
                                  {displayBuilding((shop.properties as any)["Building"] || "")}
                                </div>

                                <div
                                  className="route-favorites__actions"
                                  style={{
                                    display:
                                      this.shopsToVisit &&
                                        this.shopsToVisit.features.length >= 2
                                        ? "flex"
                                        : "none",
                                  }}
                                >
                                  <button
                                    className="route-favorites__link"
                                    onClick={() =>
                                      this.setFavShopAsOrigin(shop)
                                    }
                                  >
                                    <Icon
                                      className="icon__origin"
                                      icon="position-filled"
                                    />
                                    {getLocale().indoor.set_as_origin ||
                                      `Set as origin`}
                                  </button>

                                  <button
                                    className="route-favorites__link"
                                    onClick={() =>
                                      this.setFavShopAsDestination(shop)
                                    }
                                  >
                                    <Icon
                                      className="icon__destination"
                                      icon="marker-filled"
                                    />
                                    {getLocale().indoor.set_as_destination ||
                                      `Set as destination`}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {this.state.selectedDestination && (
                      <div className="route-field__input route-field__input--error">
                        <div className="route-field__left">
                          <Icon
                            className="icon__destination"
                            icon="marker-filled"
                          />
                          <div className="route-field__text">
                            <div className="route-field__title">
                              {destination}
                            </div>
                            <div className="route-field__subtitle">
                              {displayBuilding((selectedDestination?.properties as any)["Building"] || "")}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            {/* Footer */}
            {!showRouteBuilderSummary &&
              this.state.routeBuilderView === "default" && (
                <div className="route-panel__footer">
                  {this.controller.getWidgetMapMode() !== "favorites" && (
                    <div className="route-field">
                      <div className="route-field__label">
                        {getLocale().indoor.stop_search_label} (
                        {this.state.selectedWaypoints.length}/15)
                      </div>

                      <div className="route-search-input">
                        <div className="route-search-input__icon">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M19.125 20.4L13.15 14.425C12.65 14.7916 12.1017 15.0791 11.5052 15.2875C10.9087 15.4958 10.2739 15.6 9.60073 15.6C7.93356 15.6 6.51664 15.0166 5.34998 13.85C4.18331 12.6833 3.59998 11.2666 3.59998 9.59998C3.59998 7.93331 4.18331 6.51664 5.34998 5.34998C6.51664 4.18331 7.93331 3.59998 9.59998 3.59998C11.2666 3.59998 12.6833 4.18331 13.85 5.34998C15.0166 6.51664 15.6 7.93356 15.6 9.60073C15.6 10.2739 15.4958 10.9087 15.2875 11.5052C15.0791 12.1017 14.7916 12.65 14.425 13.15L20.4 19.125L19.125 20.4ZM9.59998 13.8C10.7666 13.8 11.7583 13.3916 12.575 12.575C13.3916 11.7583 13.8 10.7666 13.8 9.59998C13.8 8.43331 13.3916 7.44164 12.575 6.62498C11.7583 5.80831 10.7666 5.39998 9.59998 5.39998C8.43331 5.39998 7.44164 5.80831 6.62498 6.62498C5.80831 7.44164 5.39998 8.43331 5.39998 9.59998C5.39998 10.7666 5.80831 11.7583 6.62498 12.575C7.44164 13.3916 8.43331 13.8 9.59998 13.8Z"
                              fill="var(--Color-Icon-Secondary, #333333)"
                            />
                          </svg>
                        </div>
                        <div className="route-search-input__field">
                          <Autocomplete
                            defaultValue=""
                            src={(value: any) =>
                              this.autocomplete(
                                "searchedStops",
                                "searchedStopsText",
                                value,
                              )
                            }
                            keys={["name"]}
                            placeholder={getLocale().indoor.search_stop}
                            value={origin}
                            onClose={() =>
                              this.onAutoCompleteClose("searchedStopsText")
                            }
                            clearInputOnSelection={true}
                            callbackSelect={(value: any) => {
                              let selectedStop: any = null;
                              const venue =
                                this.controller._renderer.getVenue();
                              if (venue && value) {
                                if (value.id > 0) {
                                  new Promise((resolve) => {
                                    const callback = (
                                      featuresCollection: any,
                                    ) => {
                                      // eslint-disable-next-line max-len
                                      selectedStop =
                                        featuresCollection &&
                                        featuresCollection.features[0];
                                      resolve(
                                        this.setState({ selectedStop }, () => {
                                          if (value) {
                                            if (
                                              this.state.selectedWaypoints
                                                .length <
                                              getConfig().map
                                                .MAX_WAYPOINTS_ALLOWED
                                            ) {
                                              if (
                                                !this.stopExistsInRoute(
                                                  selectedStop,
                                                )
                                              ) {
                                                const updatedWaypoints = [
                                                  ...this.state
                                                    .selectedWaypoints,
                                                  selectedStop,
                                                ];
                                                this.setState({
                                                  selectedWaypoints:
                                                    updatedWaypoints,
                                                }, () => {
                                                  const container = this.panelRef?.querySelector(
                                                    ".route-panel__content",
                                                  );
                                                  if (container) {
                                                    const stops = container.querySelectorAll(".route-stop");
                                                    const lastStop = stops[stops.length - 1];
                                                    if (lastStop) {
                                                      lastStop.scrollIntoView({ behavior: "smooth", block: "nearest" });
                                                    }
                                                  }
                                                });
                                              } else {
                                                this.routeBuilderWarning =
                                                  getLocale().indoor.duplicate_stop_warning;
                                                this.setState({
                                                  showRouteBuilderWarning: true,
                                                });
                                                setTimeout(() => {
                                                  this.onHideWarningBanner();
                                                }, 3000);
                                              }

                                              value = "";
                                              this.onAutoCompleteClose(
                                                "searchedStopsText",
                                              );
                                            } else {
                                              this.routeBuilderWarning =
                                                getLocale().indoor.max_stop_limit_warning;
                                              this.setState({
                                                showRouteBuilderWarning: true,
                                              });
                                              setTimeout(() => {
                                                this.onHideWarningBanner();
                                              }, 3000);
                                            }
                                          }
                                          this.controller.trigger(
                                            this,
                                            "indoor_widget_analytics",
                                            [
                                              {
                                                event:
                                                  EVENTS.SEARCH_RESULT_SELECTED,
                                                args: selectedStop,
                                              },
                                            ],
                                          );
                                        }),
                                      );
                                    };
                                    this.indoorService.search(
                                      venue.venue_id,
                                      "",
                                      callback,
                                      undefined,
                                      value.id.toString(),
                                    );
                                  });
                                }
                              }
                            }}
                            disableAutoFocus={this.controller.getIsAutofocusDisabled()}
                            isMobileView={isMobileView()}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="route-panel__toggle">
                    <span>{getLocale().indoor.exit_gate_label}</span>
                    <Switch
                      isSmall={true}
                      id="exitgate_switch"
                      checked={showExitGates}
                      onChange={this.onExitGateOpted}
                    />
                  </div>
                  {this.state.showExitGates && (
                    <section className="route-panel__section">
                      <div className="exit-gates">
                        {this.state.allExitGates.map(
                          (gate: woosmap.map.GeoJSONFeature) => {
                            const props = gate.properties as Record<
                              string,
                              unknown
                            >;
                            const selectedGate = this.state.selectedExitGate;
                            const isSelected =
                              typeof selectedGate === "object" &&
                              selectedGate?.id === gate.id;
                            return (
                              <button
                                key={gate.id}
                                className={`exit-gate ${isSelected ? "is-selected" : ""}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  this.onExitGateSelected(gate);
                                }}
                              >
                                {(props[
                                  `name:${getLocaleLang().toLowerCase()}`
                                ] as string)
                                  ? (props[
                                    `name:${getLocaleLang().toLowerCase()}`
                                  ] as string)
                                  : (props["name"] as string)}
                              </button>
                            );
                          },
                        )}
                      </div>
                    </section>
                  )}

                  <button
                    className="route-panel__primary-btn"
                    disabled={
                      !selectedOrigin || !selectedDestination
                    }
                    onClick={this.onBuildRouteWithWaypoints}
                  >
                    {`${getLocale().indoor.optimized_route_cta_label} ( ${selectedWaypoints.length + (selectedOrigin ? 1 : 0) + (selectedDestination ? 1 : 0)} ${getLocale().indoor.steps})`}
                  </button>

                  <button
                    className="route-panel__cancel"
                    onClick={this.cancelRouteBuilding}
                  >
                    {getLocale().indoor.cancel}
                  </button>
                </div>
              )}
            {!showRouteBuilderSummary &&
              this.state.routeBuilderView === "results" && (
                <div className="route-panel__footer route-result__footer">
                  <button
                    className="route-result__cta"
                    onClick={() => {
                      this.setState({ mode: "itinerary", showPanel: true });
                    }}
                  >
                    {getLocale().indoor.start_navigation}
                  </button>

                  <button
                    className="route-result__cancel"
                    onClick={this.cancelRouteBuilding}
                  >
                    {getLocale().indoor.cancel}
                  </button>
                </div>
              )}
            {this.state.showRouteBuilderWarning && (
              <div className="route-panel__banner">
                <div className="route-panel__banner-text">
                  {this.routeBuilderWarning}
                </div>
                <button
                  className="route-panel__banner-action"
                  onClick={this.onHideWarningBanner}
                >
                  {getLocale().indoor.cancel}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
  render(): ComponentChildren {
    const { mode, isNavigationMode } = this.state;
    if (isNavigationMode) {
      return null;
    }
    return (
      <div className={`woosmap-panel ${mode}`}>
        {this.renderHeader()}
        {this.renderPanelContent()}
      </div>
    );
  }
}
